// User settings: profile (name/email/language), password change, per-channel
// subscription preferences (Telegram push toggle). Three tabs.
import { useState, useEffect, useRef } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { I } from "../icons.jsx";
import {
  Av, Btn, Inp, Modal, Tabs, Mono, Serif, useSwipeDownDismiss,
} from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT, plural } from "../i18n.jsx";
import { OrderModal } from "./OrderModal.jsx";

export function SettingsModal({ user, channels, lang, setLang, onClose, onUserUpdated, onChannelsChanged, initialTab }) {
  const toast = useToast();
  const t = useT();
  // initialTab lets callers (the onboarding checklist banner + the
  // FirstRunHint CTA) open this modal directly on the Forward-settings
  // tab instead of forcing the user to find it. Defaults to "profile"
  // when not provided so existing call sites are unchanged.
  const [tab, setTab] = useState(initialTab || "profile");
  // Profile fields — seeded from /api/me which has the full data (the
  // user object in App state only has the subset needed for routing).
  const [displayName, setDisplayName] = useState(user.displayName || "");
  const [email, setEmail] = useState(user.email || "");
  // Seeded from the lang prop (which is the active UI language from App)
  // so the Settings dropdown shows the correct current language. Any
  // change here is propagated to App via setLang on save.
  const [language, setLanguage] = useState(lang || "ru");
  // We still fetch the `forwarder` field for forward-compat with /api/me's
  // payload, but the UI for picking one was removed (low value at current
  // scale). Setter omitted on purpose.
  const [, setForwarder] = useState("");
  // Avatar state — separate from the rest of profile so its upload
  // doesn't wait on Save; uploading + saving the path happens in one
  // shot when the user picks a file.
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarFileRef = useRef(null);
  const [profileMsg, setProfileMsg] = useState("");
  const [profileErr, setProfileErr] = useState("");
  // Nickname claim — only legacy users (hasRealNickname === false) ever
  // see this UI. Once claimed the server refuses to overwrite, so we
  // hide the input on next /me refresh. nickAvail: null = not checked,
  // false = unavailable, true = ready to claim.
  const [hasRealNickname, setHasRealNickname] = useState(!!user.hasRealNickname);
  const [nickInput, setNickInput] = useState("");
  const [nickAvail, setNickAvail] = useState(null);
  const [nickReason, setNickReason] = useState("");
  const [nickChecking, setNickChecking] = useState(false);
  // Password
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch full /api/me on open so the form is pre-populated with current
  // values.
  //
  // IMPORTANT: we deliberately do NOT sync `language` from /me here. The
  // `lang` prop is the source of truth for the *active UI language*
  // (URL ?lang=en | localStorage | DB-on-boot). If we re-synced from
  // /me we'd silently overwrite the modal's local state with whatever
  // the DB stored — and a subsequent Save (which always sent `language`)
  // would then flip the entire app's language to the DB value, even
  // when the user never touched the dropdown. That was the source of
  // the "I uploaded an avatar and the UI flipped to Russian" bug.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.me();
        setDisplayName(r.user.displayName || "");
        setEmail(r.user.email || "");
        setForwarder(r.user.forwarder || "");
        setAvatarUrl(r.user.avatarUrl || null);
        setHasRealNickname(!!r.user.hasRealNickname);
      } catch (e) { setProfileErr(e.message); }
    })();
  }, []);

  // Debounced nickname availability check — runs only while the user is
  // still in the "claim" state. Same 400ms debounce as the sign-up form
  // so the two flows feel identical.
  useEffect(() => {
    if (hasRealNickname) return;
    const v = nickInput.trim().toLowerCase();
    if (!v) { setNickAvail(null); setNickReason(""); return; }
    setNickChecking(true);
    const h = setTimeout(async () => {
      try {
        const r = await api.checkNickname(v);
        setNickAvail(!!r.available);
        setNickReason(r.reason || "");
      } catch {
        setNickAvail(null);
        setNickReason("");
      } finally {
        setNickChecking(false);
      }
    }, 400);
    return () => { clearTimeout(h); setNickChecking(false); };
  }, [nickInput, hasRealNickname]);

  const claimNickname = async () => {
    setProfileErr(""); setProfileMsg("");
    const v = nickInput.trim().toLowerCase();
    if (!v || nickAvail !== true) return;
    setLoading(true);
    try {
      await api.updateMe({ nickname: v });
      setHasRealNickname(true);
      setProfileMsg(t("settings.nickname.claimed.ok", { nick: v }));
      onUserUpdated({ nickname: v, hasRealNickname: true });
    } catch (e) { setProfileErr(e.message); }
    setLoading(false);
  };

  // Upload + apply avatar in a single shot. We re-fetch /me afterwards
  // to get the canonical /media/<path> URL (the upload returns the same
  // path, but going through /me keeps the source of truth in one place).
  const onPickAvatar = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    setProfileErr(""); setProfileMsg("");
    try {
      const up = await api.uploadImage(file);
      await api.updateMe({ avatarPath: up.path });
      setAvatarUrl(up.url);
      onUserUpdated({ avatarUrl: up.url });
      setProfileMsg(t("settings.avatar.uploaded"));
    } catch (e) { setProfileErr(e.message); }
    finally { setAvatarUploading(false); if (avatarFileRef.current) avatarFileRef.current.value = ""; }
  };
  const onRemoveAvatar = async () => {
    setAvatarUploading(true);
    setProfileErr(""); setProfileMsg("");
    try {
      await api.updateMe({ avatarPath: null });
      setAvatarUrl(null);
      onUserUpdated({ avatarUrl: null });
      setProfileMsg(t("settings.avatar.removed"));
    } catch (e) { setProfileErr(e.message); }
    finally { setAvatarUploading(false); }
  };

  const saveProfile = async () => {
    setProfileErr(""); setProfileMsg(""); setLoading(true);
    try {
      // Build a diff patch — only fields the user actually changed. Sending
      // a field always (even when unchanged) silently clobbers state if
      // the local form value drifted from the truth via other means
      // (which is exactly how the avatar-upload-flipped-the-UI-to-Russian
      // bug used to happen).
      const patch = {};
      if (displayName.trim() !== user.displayName) patch.displayName = displayName.trim();
      if (email.trim().toLowerCase() !== user.email) patch.email = email.trim().toLowerCase();
      const langChanged = language !== (lang || "ru");
      if (langChanged) patch.language = language;
      if (Object.keys(patch).length === 0) {
        setProfileMsg(t("settings.saved"));
        setLoading(false);
        return;
      }
      await api.updateMe(patch);
      setProfileMsg(t("settings.saved"));
      // Only flip the app-wide language if the user actually changed the
      // dropdown — otherwise leave the active UI language alone.
      if (langChanged && setLang) setLang(language);
      onUserUpdated({ displayName: patch.displayName || user.displayName, email: patch.email || user.email });
    } catch (e) { setProfileErr(e.message); }
    setLoading(false);
  };

  const changePwd = async () => {
    setPwdErr(""); setPwdMsg(""); setLoading(true);
    try {
      if (newPwd.length < 12) throw new Error("New password must be 12+ characters");
      await api.changePassword(curPwd, newPwd);
      setPwdMsg("Password changed");
      setCurPwd(""); setNewPwd("");
    } catch (e) { setPwdErr(e.message); }
    setLoading(false);
  };

  return (
    <Modal title={t("settings.title")} onClose={onClose} width={540}>
      {/* Settings is now purely technical / account-level. Business
          surfaces — Пересылка, Клиенты, Аналитика — moved to "Мой
          магазин" (the 🏠 entry in the sidebar). Premium stays here
          because it's an account-level purchase. */}
      <Tabs
        tabs={[
          { id: "profile", label: t("settings.tab.profile") },
          { id: "password", label: t("settings.tab.password") },
          // Per-channel notification subscriptions are a reseller-side
          // concern (which catalog feeds they want pinged on). Staff
          // (admin + manager) don't subscribe to channels for buying
          // purposes — hide the tab.
          ...(user.role === "customer" ? [
            // Address book (migration 0054). Reseller-only — staff
            // don't place orders, so there's no need for them.
            { id: "addresses", label: t("settings.tab.addresses") },
            { id: "subs", label: `${t("settings.tab.subs")} (${channels.length})` },
            // Premium subscription tab hidden for now (premium disabled). The
            // PremiumTab component + admin grant/revoke stay in place, so
            // re-adding this line restores the subscription UI.
          ] : []),
        ]}
        active={tab}
        onSelect={setTab}
      />

      {tab === "profile" && (
        <div>
          {/* Avatar row — preview + change/remove buttons. Hidden file
              input is triggered by the "Upload" button. The file input
              accepts only images; the upload pipeline (sharp + EXIF
              strip) handles re-encoding regardless of source format. */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: T_NEW.s6,
                        background: T_NEW.paperSoft, padding: 16, borderRadius: T_NEW.r2,
                        border: `1px solid ${T_NEW.ink12}` }}>
            <Av name={displayName} src={avatarUrl} size={72} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
                {t("settings.avatar.label")}
              </Mono>
              <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginBottom: 10,
                            fontFamily: T_NEW.fontSerif, fontStyle: "italic", lineHeight: 1.5 }}>
                {t("settings.avatar.hint")}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn variant="ghost" disabled={avatarUploading}
                     onClick={() => avatarFileRef.current?.click()}>
                  {avatarUploading ? t("common.loading") : (avatarUrl ? t("settings.avatar.change") : t("settings.avatar.upload"))}
                </Btn>
                {avatarUrl && (
                  <Btn variant="ghost" disabled={avatarUploading} onClick={onRemoveAvatar}>
                    {t("settings.avatar.remove")}
                  </Btn>
                )}
              </div>
              <input ref={avatarFileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                     style={{ display: "none" }}
                     onChange={(e) => onPickAvatar(e.target.files?.[0])} />
            </div>
          </div>

          {/* Public handle. Two states:
              · hasRealNickname → read-only pill (set once, support
                changes only).
              · !hasRealNickname → one-time claim widget for legacy
                accounts created before migration 0051; their public
                identifier is currently a synthetic user_<prefix> and
                this is where they pick a real one. */}
          {hasRealNickname ? (
            <div style={{ marginBottom: T_NEW.s4 }}>
              <Mono size={T_NEW.micro} color={T_NEW.ink56}
                    style={{ display: "block", marginBottom: 6 }}>
                {t("settings.nickname.public")}
              </Mono>
              <div style={{ padding: "12px 14px",
                            background: T_NEW.paperSoft,
                            border: `1px solid ${T_NEW.ink12}`,
                            borderRadius: T_NEW.r1,
                            fontSize: 15, color: T_NEW.ink,
                            fontFamily: T_NEW.fontMono }}>
                @{user.nickname}
              </div>
              <Mono color={T_NEW.ink38} size={T_NEW.micro}
                    style={{ display: "block", marginTop: 4 }}>
                {t("settings.nickname.publicHint")}
              </Mono>
            </div>
          ) : (
            <div style={{ marginBottom: T_NEW.s4,
                          padding: 14,
                          background: `${T_NEW.accent}0F`,
                          border: `1px dashed ${T_NEW.accent}66`,
                          borderRadius: T_NEW.r2 }}>
              <Mono size={T_NEW.micro} color={T_NEW.ink}
                    style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
                {t("settings.nickname.claim.title")}
              </Mono>
              <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                            fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                            lineHeight: 1.5, marginBottom: 10 }}>
                {t("settings.nickname.claim.hint", { nick: user.nickname })}
              </div>
              <Inp
                value={nickInput}
                onChange={(v) => setNickInput(v.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder={t("settings.nickname.claim.placeholder")}
                inputMode="text"
                autoComplete="off"
                rightSlot={
                  nickChecking
                    ? <span style={{ color: T_NEW.ink56, fontSize: 12 }}>…</span>
                    : nickInput && nickAvail === true
                      ? <span style={{ color: T_NEW.success, fontSize: 16 }}>✓</span>
                      : nickInput && nickAvail === false
                        ? <span style={{ color: T_NEW.danger, fontSize: 16 }}>✗</span>
                        : null
                }
              />
              {nickInput && nickAvail === false && (
                <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginTop: -6, marginBottom: 8 }}>
                  {nickReason === "reserved" ? t("settings.nickname.err.reserved")
                    : nickReason === "taken" ? t("settings.nickname.err.taken")
                    : t("settings.nickname.err.format")}
                </div>
              )}
              <Btn variant="primary"
                   disabled={loading || nickAvail !== true}
                   onClick={claimNickname}>
                {t("settings.nickname.claim.cta")}
              </Btn>
            </div>
          )}
          <Inp label={t("settings.fullName.label")} value={displayName} onChange={setDisplayName} placeholder={t("auth.displayName.placeholder")} icon={I.users()} />
          <Inp label={t("settings.email")} value={email} onChange={setEmail} placeholder="you@example.com" icon={I.mail()} />
          <div style={{ marginBottom: T_NEW.s4 }}>
            <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
              {t("settings.language")}
            </Mono>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}
                    style={{ width: "100%", background: T_NEW.paper, color: T_NEW.ink,
                             border: `1px solid ${T_NEW.ink12}`, padding: "12px 14px",
                             borderRadius: T_NEW.r1, fontSize: 15, fontFamily: T_NEW.fontSans }}>
              <option value="ru">{t("settings.language.ru")}</option>
              <option value="en">{t("settings.language.en")}</option>
              <option value="es">{t("settings.language.es")}</option>
            </select>
          </div>
          {profileErr && (
            <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginBottom: 12,
                          padding: "10px 12px", background: `${T_NEW.danger}14`,
                          border: `1px solid ${T_NEW.danger}33`, borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontSans }}>
              {profileErr}
            </div>
          )}
          {profileMsg && (
            <div style={{ color: T_NEW.bottle, fontSize: T_NEW.meta, marginBottom: 12,
                          padding: "10px 12px", background: `${T_NEW.bottle}12`,
                          border: `1px solid ${T_NEW.bottle}33`, borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontSans }}>
              ✓ {profileMsg}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn disabled={loading} onClick={saveProfile}>{loading ? t("settings.saving") : t("settings.save")}</Btn>
          </div>
        </div>
      )}

      {tab === "password" && (
        <div>
          <Inp label={t("settings.curPassword")} type="password" value={curPwd} onChange={setCurPwd} placeholder="••••••••" icon={I.lock()} />
          <Inp label={t("settings.newPassword")} type="password" value={newPwd} onChange={setNewPwd} placeholder="••••••••" icon={I.lock()} />
          {pwdErr && (
            <div style={{ color: T_NEW.danger, fontSize: T_NEW.meta, marginBottom: 12,
                          padding: "10px 12px", background: `${T_NEW.danger}14`,
                          border: `1px solid ${T_NEW.danger}33`, borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontSans }}>
              {pwdErr}
            </div>
          )}
          {pwdMsg && (
            <div style={{ color: T_NEW.bottle, fontSize: T_NEW.meta, marginBottom: 12,
                          padding: "10px 12px", background: `${T_NEW.bottle}12`,
                          border: `1px solid ${T_NEW.bottle}33`, borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontSans }}>
              ✓ {pwdMsg}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn disabled={loading || !curPwd || !newPwd} onClick={changePwd}>{loading ? t("settings.changing") : t("settings.changePassword")}</Btn>
          </div>
        </div>
      )}

      {tab === "subs" && (
        <div>
          <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginBottom: 14,
                        fontFamily: T_NEW.fontSerif, fontStyle: "italic", lineHeight: 1.5 }}>
            {t("settings.subs.help")}
          </div>
          {channels.length === 0 ? (
            <div style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, padding: 18, textAlign: "center",
                          background: T_NEW.paperSoft, borderRadius: T_NEW.r1,
                          fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
              {t("settings.subs.empty")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {channels.map(ch => {
                const sub = ch.subscription || { digest: true, telegramPush: true };
                return (
                  <div key={ch.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                                             padding: "12px 14px", background: T_NEW.paperSoft,
                                             borderRadius: T_NEW.r1, border: `1px solid ${T_NEW.ink12}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: T_NEW.fontSerif, fontSize: T_NEW.bodySm, fontWeight: 500,
                                    color: T_NEW.ink, lineHeight: 1.25 }}>
                        {ch.name}
                      </div>
                      <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 3,
                                    fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                                    letterSpacing: T_NEW.monoTrack }}>
                        {ch.publishedCount} {t(plural(ch.publishedCount, "settings.subs.posts"))}
                      </div>
                    </div>
                    <label style={{ display: "flex", alignItems: "center", gap: 8,
                                    fontSize: T_NEW.micro, color: T_NEW.ink56, cursor: "pointer",
                                    fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                                    letterSpacing: T_NEW.monoTrack, fontWeight: 600 }}>
                      <input type="checkbox" checked={sub.telegramPush}
                             onChange={async (e) => {
                               try {
                                 await api.setChannelSubscription(ch.id, { digest: sub.digest, telegramPush: e.target.checked });
                                 onChannelsChanged();
                               } catch (err) { toast.error(err.message); }
                             }}
                             style={{ accentColor: T_NEW.persimmon, width: 16, height: 16 }} />
                      {t("settings.subs.tgPush")}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* "forward", "customers", "stats" tabs moved to MyStore. The
          components are still exported from this file so MyStore can
          import them — the renderers just don't fire here anymore. */}
      {tab === "addresses" && <AddressBookTab />}
      {tab === "premium" && <PremiumTab user={user} />}
    </Modal>
  );
}

// ─── AddressBookTab — reseller's own saved addresses (0054) ───────
//
// CRUD over /api/me/addresses. Default-flagged entries float to the
// top of the list. Editing happens inline — clicking "Изменить" on
// a row swaps it into an edit form; saving PATCHes and folds the
// row back into read mode.
//
// Style: matches the simple-list pattern used by other Settings
// surfaces (no DataGrid, no virtualization — a reseller's address
// book is small).
function AddressBookTab() {
  const t = useT();
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [editingId, setEditingId] = useState(null); // id or "new"
  // Form state (used for both new + edit).
  const [form, setForm] = useState(blankAddressForm(t));
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRows(await api.listMyAddresses()); }
    catch (e) { toast.error(e.message); setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm(blankAddressForm(t));
    setEditingId("new");
  };
  const startEdit = (row) => {
    setForm({
      label:        row.label || "",
      recipientName:row.recipientName || "",
      phone:        row.phone || "",
      address:      row.address || "",
      country:      row.country || "RU",
      isDefault:    !!row.isDefault,
    });
    setEditingId(row.id);
  };
  const cancelEdit = () => { setEditingId(null); };

  const save = async () => {
    if (!form.recipientName.trim() || !form.address.trim()) {
      toast.error(t("order.recipient.required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        recipientName: form.recipientName.trim(),
        phone: form.phone.trim() || null,
        address: form.address.trim(),
        country: form.country.trim() || "RU",
        isDefault: !!form.isDefault,
      };
      if (editingId === "new") {
        await api.createMyAddress(payload);
      } else {
        await api.updateMyAddress(editingId, payload);
      }
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("settings.addresses.deleteConfirm"))) return;
    try {
      await api.deleteMyAddress(id);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const setDefault = async (id) => {
    try {
      await api.updateMyAddress(id, { isDefault: true });
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <Serif size={18} color={T_NEW.ink} style={{ display: "block", marginBottom: 4 }}>
        {t("settings.addresses.title")}
      </Serif>
      <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56,
                    fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                    marginBottom: 16, lineHeight: 1.5 }}>
        {t("settings.addresses.subtitle")}
      </div>

      {rows == null ? (
        <div style={{ padding: 20, textAlign: "center", color: T_NEW.ink56 }}>…</div>
      ) : rows.length === 0 && editingId !== "new" ? (
        <div style={{ padding: 24, background: T_NEW.paperSoft,
                      border: `1px dashed ${T_NEW.ink12}`,
                      borderRadius: T_NEW.r2,
                      fontSize: 13, color: T_NEW.ink56,
                      fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                      textAlign: "center", marginBottom: 14 }}>
          {t("settings.addresses.empty")}
        </div>
      ) : (
        rows.map((a) => editingId === a.id ? (
          <AddressForm key={a.id} form={form} setForm={setForm}
                       saving={saving}
                       onSave={save} onCancel={cancelEdit} />
        ) : (
          <AddressRow key={a.id} row={a} badgeKey="isDefault"
                      badgeLabel={t("settings.addresses.default")}
                      onEdit={() => startEdit(a)}
                      onDelete={() => remove(a.id)}
                      onMakeDefault={a.isDefault ? null : () => setDefault(a.id)}
                      setDefaultLabel={t("settings.addresses.setDefault")} />
        ))
      )}

      {editingId === "new" && (
        <AddressForm form={form} setForm={setForm}
                     saving={saving}
                     onSave={save} onCancel={cancelEdit} />
      )}

      {editingId == null && (
        <Btn variant="ghost" onClick={startNew}
             style={{ marginTop: 10 }}>
          + {t("settings.addresses.add")}
        </Btn>
      )}
    </div>
  );
}

// Empty address form template. Country defaults to RU.
function blankAddressForm() {
  return {
    label: "",
    recipientName: "",
    phone: "",
    address: "",
    country: "RU",
    isDefault: false,
  };
}

// AddressRow — read-mode row in the book. Shared between the
// Settings tab and CustomerDetailModal so the visual treatment of
// "saved address card" is consistent across the app.
export function AddressRow({ row, badgeKey, badgeLabel,
                             onEdit, onDelete, onMakeDefault,
                             setDefaultLabel }) {
  const t = useT();
  return (
    <div style={{ padding: 12, marginBottom: 8,
                  background: T_NEW.paperRaised,
                  border: `1px solid ${T_NEW.ink12}`,
                  borderRadius: T_NEW.r2 }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 4, gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: T_NEW.ink,
                       fontFamily: T_NEW.fontSans, flex: 1, minWidth: 0,
                       overflow: "hidden", textOverflow: "ellipsis",
                       whiteSpace: "nowrap" }}>
          {row.label || row.recipientName}
        </span>
        {row[badgeKey] && (
          <span style={{ fontSize: 10, fontWeight: 700,
                         letterSpacing: 0.4, textTransform: "uppercase",
                         color: T_NEW.accent, flexShrink: 0 }}>
            {badgeLabel}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: T_NEW.ink56, lineHeight: 1.45 }}>
        {row.recipientName}{row.phone ? ` · ${row.phone}` : ""}
      </div>
      <div style={{ fontSize: 12, color: T_NEW.ink56, lineHeight: 1.45,
                    marginBottom: 8 }}>
        {row.address}{row.country && row.country !== "RU" ? ` · ${row.country}` : ""}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" onClick={onEdit}
                style={miniBtn(T_NEW.ink56)}>
          {t("settings.addresses.edit")}
        </button>
        {onMakeDefault && (
          <button type="button" onClick={onMakeDefault}
                  style={miniBtn(T_NEW.accent)}>
            {setDefaultLabel}
          </button>
        )}
        <button type="button" onClick={onDelete}
                style={miniBtn(T_NEW.danger)}>
          {t("settings.addresses.delete")}
        </button>
      </div>
    </div>
  );
}

function miniBtn(color) {
  return {
    background: "transparent",
    border: "none",
    color,
    cursor: "pointer",
    fontSize: 12, fontWeight: 600,
    fontFamily: "inherit",
    padding: "2px 0",
    textDecoration: "underline",
  };
}

// AddressForm — shared add/edit form for both books. Caller wires
// the save/cancel handlers and chooses whether to expose the
// "is default" / "is primary" checkbox (we expose it as
// `form.isDefault` and label it from i18n).
export function AddressForm({ form, setForm, saving, onSave, onCancel,
                              primaryLabel }) {
  const t = useT();
  return (
    <div style={{ padding: 14, marginBottom: 10,
                  background: T_NEW.paperSoft,
                  border: `1px solid ${T_NEW.ink12}`,
                  borderRadius: T_NEW.r2 }}>
      <Inp label={t("settings.addresses.label")}
           value={form.label}
           onChange={(v) => setForm({ ...form, label: v })}
           placeholder={t("settings.addresses.labelPh")} />
      <Inp label={t("settings.addresses.recipient") + " *"}
           value={form.recipientName}
           onChange={(v) => setForm({ ...form, recipientName: v })} />
      <Inp label={t("settings.addresses.phone")}
           value={form.phone}
           onChange={(v) => setForm({ ...form, phone: v })} />
      <div style={{ marginBottom: 12 }}>
        <Mono style={{ display: "block", marginBottom: 6 }}>
          {t("settings.addresses.address")} *
        </Mono>
        <textarea
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          style={{ width: "100%", background: T_NEW.paper,
                   border: `1px solid ${T_NEW.ink12}`,
                   color: T_NEW.ink, padding: "10px 12px",
                   borderRadius: 10, fontSize: 14, minHeight: 56,
                   resize: "vertical", fontFamily: "inherit" }} />
      </div>
      <Inp label={t("settings.addresses.country")}
           value={form.country}
           onChange={(v) => setForm({ ...form, country: v })} />
      <label style={{ display: "flex", alignItems: "center", gap: 8,
                      marginBottom: 14, cursor: "pointer",
                      fontSize: 13, color: T_NEW.ink }}>
        <input type="checkbox" checked={!!form.isDefault}
               onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
        <span>{primaryLabel || t("settings.addresses.setDefault")}</span>
      </label>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onCancel}>
          {t("settings.addresses.cancel")}
        </Btn>
        <Btn disabled={saving} onClick={onSave}>
          {saving ? "…" : t("settings.addresses.save")}
        </Btn>
      </div>
    </div>
  );
}


// ─── ForwardSettingsTab — reseller forward config ────────────────────
// Designed for non-technical bulk-buy resellers. Three sections:
//
//   1. FX rate — single number input. ₽ per $1, used to convert the
//      USD prices on our cards to ₽ in the forward caption.
//   2. Content of forward caption — checkboxes for which fields to
//      include (brand, model, ₽-price, discount %, description).
//      DIRECT_LINK is intentionally excluded (it leaks the source $
//      price to the reseller's audience, undercutting their markup).
//   3. Forward targets — list of Telegram channels they own where
//      @Buyergrambot must be admin. MAX support stubbed.
//
// Internally we still store the choice as a Mustache-style template
// string (back-compat with the API) but generate it from the checkbox
// state so the user never sees the syntax.
export function ForwardSettingsTab({ toast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fxRate, setFxRate] = useState("");
  const [include, setInclude] = useState({
    brand: true,
    title: true,
    priceRub: true,
    discount: true,
    description: true,
    // Internal BG code (publicCode). On by default per 2026-06-05
    // operator decision — every checkbox in the caption builder
    // ships pre-checked. Turning it on lets the reseller's downstream
    // customer reference the item back to BuyerGram via 'Найти по
    // коду' for repeat orders / questions.
    publicCode: true,
  });
  // Free-form text appended after the template-rendered caption. Kept
  // separate from `include`/`templateFromInclude` so it survives
  // checkbox toggles (the template is regenerated from include state
  // on every save — splicing custom text into the template directly
  // would lose it on the next toggle).
  const [customText, setCustomText] = useState("");
  // Caption layout preset (migration 0058). One of: terse | standard |
  // editorial | discount | group_buy. Drives templateFromIncludeAndStyle
  // — the include checkboxes still control WHICH fields appear; this
  // controls the LAYOUT of those fields. Persisted server-side so the
  // chosen preset survives across devices.
  const [style, setStyle] = useState("standard");
  const [targets, setTargets] = useState([]);

  // Build a server-side template string from the include checkboxes.
  // Each field is one line; the {{variable}} placeholders are expanded
  // server-side at forward time using the user's FX rate. We keep this
  // mapping in sync with the parsing below — if you add a field, do it
  // in both functions.
  // ─── Caption layout presets (migration 0058) ──────────────────────
  // 5 named styles. Each style respects the 6 field-include checkboxes
  // (brand/title/priceRub/discount/description/publicCode) but renders
  // the layout differently. MSRP rides along with the discount
  // checkbox — discount % only reads as a real savings hook with the
  // crossed-out retail next to it.
  //
  // Adding a style:
  //   1. add an entry here
  //   2. add the matching options + i18n keys
  //   3. add the CHECK constraint value in migration 0058
  //   4. add the ALLOWED_CAPTION_STYLES entry in apps/api/src/routes/forwards.ts
  // Each style is distinguished by its dominant visual pattern, not by
  // punctuation alone — so a customer flipping through a channel can
  // tell at a glance which template a reseller picked. Patterns lifted
  // from observed Russian US-delivery channels (LiteMF, Shopfans).
  //
  //   terse      — 2 dense lines, no description, no MSRP
  //   standard   — ✔️ checkmark bullets for spec scanning
  //   editorial  — prose paragraph with personality, no lists
  //   discount   — "−X% · BRAND" bold headline, past/present price split
  //   group_buy  — labelled SP sections (min / deadline / how-to-join)
  const STYLE_RENDERERS = {
    // None — no template at all. Caption comes entirely from the
    // free-form custom-text field below. The reseller writes whatever
    // they want; the checkbox builder is hidden when this is chosen.
    // The custom-text field still supports {{variables}} so they can
    // splice in {{brand}}, {{price_rub}}, etc. by hand.
    none() {
      return "";
    },

    // Terse — 2 lines. Brand · title · price · −%, then code. For
    // catalog channels where customers know the format and want the
    // info at a glance. No description, no MSRP — those add noise
    // when the picture already shows the product.
    terse(inc) {
      const lines = [];
      const head = [];
      if (inc.brand) head.push("{{brand}}");
      if (inc.title) head.push("{{title}}");
      if (head.length) lines.push(head.join(" · "));
      const tail = [];
      if (inc.priceRub) tail.push("{{price_rub}} ₽");
      if (inc.discount) tail.push("−{{discount_pct}}%");
      if (tail.length) lines.push(tail.join(" · "));
      if (inc.publicCode) lines.push("🔖 {{public_code}}");
      return lines.join("\n");
    },

    // Standard — ✔️ checkmark bullets. Each field on its own line with
    // a checkmark, the dominant pattern on LiteMF and similar US-
    // delivery channels. Easy for customers to scan because the eye
    // catches the bullet, not the prose. Distinct from editorial
    // because it's a LIST, not a paragraph.
    standard(inc) {
      const lines = [];
      const head = [];
      if (inc.brand) head.push("{{brand}}");
      if (inc.title) head.push("{{title}}");
      if (head.length) lines.push(head.join(" · "));
      if (lines.length) lines.push("");
      if (inc.priceRub) lines.push("✔️ Цена: {{price_rub}} ₽");
      if (inc.discount) {
        lines.push("✔️ Розница: {{msrp_rub}} ₽");
        lines.push("✔️ Скидка: −{{discount_pct}}%");
      }
      if (inc.description) lines.push("✔️ {{description}}");
      if (inc.publicCode) {
        lines.push("", "📦 Заказ — в личку", "🔖 {{public_code}}");
      }
      return lines.join("\n");
    },

    // Editorial — prose paragraph with personality. No bullets, no
    // emoji clutter. The reseller speaks in first person ("у меня
    // выгоднее", "помогу подобрать"). Distinct because it READS like
    // a paragraph, not a list — the whole point of editorial voice.
    editorial(inc) {
      const lines = [];
      if (inc.brand && inc.title) lines.push("Свежий завоз: {{brand}} {{title}}.");
      else if (inc.brand) lines.push("Свежий завоз: {{brand}}.");
      else if (inc.title) lines.push("Свежий завоз: {{title}}.");
      if (inc.description) {
        if (lines.length) lines.push("");
        lines.push("{{description}}");
      }
      // Price prose line — varies by what's enabled to keep it grammatical.
      if (inc.priceRub || inc.discount) {
        if (lines.length) lines.push("");
        if (inc.priceRub && inc.discount) {
          lines.push("Розница в США — {{msrp_rub}} ₽, у меня выгоднее на {{discount_pct}}%: {{price_rub}} ₽.");
        } else if (inc.priceRub) {
          lines.push("У меня цена — {{price_rub}} ₽.");
        } else if (inc.discount) {
          lines.push("Сейчас выгоднее на {{discount_pct}}%.");
        }
      }
      lines.push("", "Цвета и размеры разные, пишите — помогу подобрать.");
      if (inc.publicCode) {
        lines.push("", "Артикул: {{public_code}}");
      }
      return lines.join("\n");
    },

    // Discount-forward — bold "−X% · BRAND" headline as the first line,
    // mirroring LiteMF's "Летний сейл от Michael Kors" headline pattern.
    // Price split into "Сейчас" (now) and "Было" (was) two-line block
    // for instant visual contrast. Distinct because the % is the LEAD,
    // not buried mid-template.
    discount(inc) {
      const lines = [];
      const head = [];
      if (inc.discount) head.push("🔥 −{{discount_pct}}%");
      if (inc.brand) head.push("{{brand}}");
      if (head.length) lines.push(head.join(" · "));
      // Title as its own emphasis line directly under the headline.
      if (inc.title) lines.push("{{title}}");
      if (inc.description) {
        if (lines.length) lines.push("");
        lines.push("{{description}}");
      }
      // Two-line price contrast — the visual gut-punch of the layout.
      if (inc.priceRub || inc.discount) {
        if (lines.length) lines.push("");
        if (inc.priceRub) lines.push("Сейчас: {{price_rub}} ₽");
        if (inc.discount) lines.push("Было: {{msrp_rub}} ₽");
      }
      if (inc.publicCode) {
        lines.push("", "⏳ Размеры разбираются — пишите в личку", "🔖 {{public_code}}");
      }
      return lines.join("\n");
    },

    // СП — ALL CAPS opener "СОВМЕСТНАЯ ЗАКУПКА" pulls eyes, then
    // labelled spec lines for the SP-specific concerns: group price,
    // retail comparison, minimum participants, deadline window, how
    // to record interest. Resellers edit the 5-person / уточняйте-в-
    // шапке defaults per-batch. Distinct because no other style has
    // the SP scaffolding at all.
    group_buy(inc) {
      const lines = ["СОВМЕСТНАЯ ЗАКУПКА", ""];
      const head = [];
      if (inc.brand) head.push("{{brand}}");
      if (inc.title) head.push("{{title}}");
      if (head.length) lines.push(head.join(" · "));
      if (inc.description) {
        lines.push("{{description}}");
      }
      if (inc.priceRub || inc.discount) {
        lines.push("");
        if (inc.priceRub) lines.push("💰 Цена в группе: {{price_rub}} ₽");
        if (inc.discount) lines.push("🏷 Розница: {{msrp_rub}} ₽ (выгода {{discount_pct}}%)");
      }
      lines.push(
        "",
        "👥 Минимум: 5 человек",
        "⏰ Сбор до: уточняйте в шапке канала",
        "",
        "Записаться — «+» в комментариях",
      );
      if (inc.publicCode) {
        lines.push("🔖 {{public_code}}");
      }
      return lines.join("\n");
    },
  };

  function templateFromIncludeAndStyle(inc, style) {
    const renderer = STYLE_RENDERERS[style] || STYLE_RENDERERS.standard;
    return renderer(inc);
  }

  // Legacy single-style renderer — kept as a thin alias for the
  // existing call sites so this change is reviewable in isolation.
  // The active call sites below pass `style` explicitly.
  function templateFromInclude(inc) {
    return templateFromIncludeAndStyle(inc, style);
  }

  // Reverse: parse a template string into checkbox state. Used on load
  // so previously-saved templates roughly reflect in the UI.
  //
  // publicCode (the BG internal code) is force-defaulted to true even
  // when the saved template lacks {{public_code}}. Reasoning:
  //   - The BG code is the customer's only way to reference a forwarded
  //     item back to BuyerGram (catalog has no other identifier for
  //     them — upstream SKU is hidden by design).
  //   - Pre-2026-06-05 templates were generated before the publicCode
  //     checkbox existed, so a parse that strictly checks for
  //     {{public_code}} would surface FALSE on every legacy row and the
  //     code would silently disappear from future forwards.
  // A reseller who wants it off can uncheck it explicitly; we'd rather
  // err toward keeping it visible on every caption.
  function includeFromTemplate(tpl) {
    if (!tpl) return {
      brand: true, title: true, priceRub: true,
      discount: true, description: true, publicCode: true,
    };
    return {
      brand: tpl.includes("{{brand}}"),
      title: tpl.includes("{{title}}"),
      priceRub: tpl.includes("{{price_rub}}"),
      discount: tpl.includes("{{discount_pct}}"),
      description: tpl.includes("{{description}}"),
      // Force-on: see comment block above. The BG code stays on
      // unless the reseller explicitly unchecks and saves a template
      // that re-omits {{public_code}} — at that point the absence is
      // intentional and we honor it on the next load (the checkbox
      // detection still respects an actively-unchecked save because
      // by then the saved template won't have {{public_code}} AND
      // no other field will be either if the user toggled them off).
      // Tradeoff: a user who saves the template then unchecks "Код"
      // and saves AGAIN gets the code back on the next load. That's
      // acceptable given the BG code is core to how customers
      // reference items downstream.
      publicCode: true,
    };
  }

  useEffect(() => {
    (async () => {
      try {
        const s = await api.getResellerSettings();
        setFxRate(s.fxRateRubPerUsd > 0 ? String(s.fxRateRubPerUsd) : "");
        setInclude(includeFromTemplate(s.captionTemplate || ""));
        // Server defaults captionCustomText to "" on rows that existed
        // before migration 0036, so the ?? "" guard is belt-and-suspenders.
        setCustomText(s.captionCustomText ?? "");
        setStyle(s.captionStyle || "standard");
        setTargets(s.forwardTargets ?? []);
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async (patch, msg) => {
    setSaving(true);
    try {
      const r = await api.patchResellerSettings(patch);
      setFxRate(r.fxRateRubPerUsd > 0 ? String(r.fxRateRubPerUsd) : "");
      setInclude(includeFromTemplate(r.captionTemplate || ""));
      setCustomText(r.captionCustomText ?? "");
      setStyle(r.captionStyle || "standard");
      setTargets(r.forwardTargets ?? []);
      toast.success(msg || "Сохранено");
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const toggleInclude = (key) =>
    setInclude((prev) => ({ ...prev, [key]: !prev[key] }));

  // Stable random-ish ID for new target rows. Crypto.randomUUID is
  // widely available in modern browsers; fall back to a timestamp if
  // not (e.g. ancient WebView).
  const newTargetId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `t_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const addTarget = (kind) => {
    setTargets((prev) => [
      ...prev,
      { id: newTargetId(), kind, chatId: "", label: "", enabled: true },
    ]);
  };
  const removeTarget = (id) => setTargets((prev) => prev.filter((t) => t.id !== id));
  const updateTarget = (id, patch) =>
    setTargets((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  // Raw input styles for the FX rate and channel-config fields. The
  // shared `Inp` component swallows arbitrary props (no width override,
  // no number-input attributes), which is why an earlier version of
  // the FX field couldn't accept typed digits — Inp re-rendered the
  // value from props but didn't propagate the value the user typed.
  // Using a styled raw <input> avoids that whole class of bug.
  const inputStyle = {
    padding: "8px 12px",
    border: `1px solid ${T_NEW.ink12}`,
    borderRadius: T_NEW.r1,
    fontFamily: T_NEW.fontSans,
    fontSize: T_NEW.body,
    background: T_NEW.paperRaised,
    color: T_NEW.ink,
  };

  if (loading) return <p style={{ color: T_NEW.ink56, padding: 16 }}>Загрузка…</p>;

  return (
    <div style={{ padding: "8px 0" }}>
      {/* ── 1. FX rate ───────────────────────────────────────────── */}
      <section style={{ marginBottom: 24 }}>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          Курс $ → ₽
        </Mono>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            type="number" inputMode="decimal" step="0.01" min="0"
            placeholder="92.45"
            value={fxRate}
            onChange={(e) => setFxRate(e.target.value)}
            style={{ ...inputStyle, width: 120 }}
          />
          <span style={{ color: T_NEW.ink56, fontSize: T_NEW.meta }}>₽ за $1</span>
          <Btn variant="accent" disabled={saving}
               onClick={() => save({ fxRateRubPerUsd: parseFloat(fxRate || "0") }, "Курс сохранён")}>
            Сохранить
          </Btn>
        </div>
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, marginTop: 6 }}>
          Цена в подписи пересылки = $-цена × этот курс. Поставьте свой курс с наценкой.
        </p>
      </section>

      {/* ── 2. Caption content (style preset + checkboxes + custom text) */}
      <section style={{ marginBottom: 24 }}>
        {/* Style preset chips — picks the LAYOUT. The 6 checkboxes
            below still control WHICH fields appear. Each chip swaps the
            chosen layout function; live preview updates instantly.
            Persisted server-side (migration 0058) so the choice
            survives reload + cross-device. */}
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          Стиль подписи
        </Mono>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
          {[
            { key: "none",      label: "Без шаблона", hint: "Свой текст без структуры — полностью свободная подпись" },
            { key: "terse",     label: "Лаконичный",  hint: "2 строки + код. Для каталога" },
            { key: "standard",  label: "Чек-лист",    hint: "✔️ построчно — удобно сканировать" },
            { key: "editorial", label: "Авторский",   hint: "Прозой, с голосом и характером" },
            { key: "discount",  label: "Скидка",      hint: "−% в заголовке, Сейчас/Было" },
            { key: "group_buy", label: "СП",          hint: "Совместная закупка с минимумом" },
          ].map((s) => {
            const isActive = s.key === style;
            return (
              <button key={s.key} onClick={() => setStyle(s.key)} title={s.hint}
                      style={{
                        padding: "6px 12px", borderRadius: 16,
                        border: `1px solid ${isActive ? T_NEW.persimmon : T_NEW.ink12}`,
                        background: isActive ? T_NEW.persimmon : "transparent",
                        color: isActive ? "#fff" : T_NEW.ink,
                        cursor: "pointer",
                        fontFamily: T_NEW.fontMono,
                        fontSize: T_NEW.micro,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: T_NEW.monoTrack,
                      }}>
                {s.label}
              </button>
            );
          })}
        </div>
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: -6, marginBottom: 14, lineHeight: 1.5 }}>
          {{
            none:      "Шаблон не применяется. Подпись формируется только из поля «Свой текст» ниже — пишите как угодно, поддерживаются переменные {{brand}} {{title}} {{price_rub}} {{msrp_rub}} {{discount_pct}} {{public_code}}.",
            terse:     "Две плотные строки: бренд · модель и цена · −%. Без описания и розницы — картинка уже говорит сама. Хорошо ложится на каналы-каталоги с большим потоком.",
            standard:  "Каждое поле — отдельная строка с галочкой ✔️. Доминирующий формат в крупных шопинг-каналах (LiteMF, Shopfans) — глаз цепляется за маркер, читать не обязательно.",
            editorial: "Свободная проза от первого лица: «у меня выгоднее», «помогу подобрать». Без буллетов и эмодзи-частокола. Подходит каналам-клубам, где важна узнаваемая интонация автора.",
            discount:  "Шапка с большим «🔥 −X% · BRAND», затем модель строкой ниже и пара «Сейчас / Было». Скидка — главное визуальное событие поста.",
            group_buy: "Открывается заглавным «СОВМЕСТНАЯ ЗАКУПКА», дальше цена в группе, розница и блок СП-механики: минимум участников, дедлайн, способ записи. Цифры можно править под каждый сбор.",
          }[style]}
        </p>

        {/* Field-include checkboxes — hidden when the "Без шаблона"
            style is chosen, because no checkbox combination produces
            output then (the renderer returns an empty string). Showing
            them anyway just confuses the reseller — "why doesn't the
            preview update when I toggle these?" */}
        {style !== "none" && (
          <>
            <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
              Что показывать в подписи к карточкам
            </Mono>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 8,
              background: T_NEW.paperSoft,
              padding: 12,
              borderRadius: T_NEW.r1,
            }}>
              <Toggle label="Бренд"          checked={include.brand}       onChange={() => toggleInclude("brand")} />
              <Toggle label="Модель"         checked={include.title}       onChange={() => toggleInclude("title")} />
              <Toggle label="Цена в ₽"       checked={include.priceRub}    onChange={() => toggleInclude("priceRub")} />
              <Toggle label="Скидка %"       checked={include.discount}    onChange={() => toggleInclude("discount")} />
              <Toggle label="Описание"       checked={include.description} onChange={() => toggleInclude("description")} />
              <Toggle label="Внутренний код" checked={include.publicCode}  onChange={() => toggleInclude("publicCode")} />
            </div>
            <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, marginTop: 6, lineHeight: 1.5 }}>
              Прямая ссылка на товар не пересылается — иначе клиенты увидят
              оригинальную $-цену и обойдут вашу наценку.
            </p>
          </>
        )}

        {/* Free-form custom text — appended after the template-rendered
            block. Same {{variables}} as the template are supported so a
            reseller can splice price/brand into their custom block (e.g.
            "🔥 Только сегодня: {{brand}} {{title}} за ₽{{price_rub}}"). */}
        <div style={{ marginTop: 14 }}>
          <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
            {style === "none" ? "Текст подписи" : "Свой текст (необязательно)"}
          </Mono>
          <textarea
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            rows={style === "none" ? 6 : 3}
            placeholder={style === "none"
              ? "Пишите как угодно — это будет полная подпись каждой пересылки.\nМожно вставлять переменные, например:\n\n{{brand}} {{title}}\nЦена у меня: {{price_rub}} ₽ (розница {{msrp_rub}} ₽, −{{discount_pct}}%)\n\nЗаказ — @your_handle\nКод: {{public_code}}"
              : "Например:\n📦 Доставка из США 14 дней. Заказать — пишите в личку."}
            style={{
              ...inputStyle,
              width: "100%",
              resize: "vertical",
              fontFamily: T_NEW.fontSans,
              lineHeight: 1.5,
            }}
          />
          <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4, lineHeight: 1.5 }}>
            Можно использовать переменные:&nbsp;
            <code>{"{{brand}}"}</code>&nbsp;
            <code>{"{{title}}"}</code>&nbsp;
            <code>{"{{price_rub}}"}</code>&nbsp;
            <code>{"{{msrp_rub}}"}</code>&nbsp;
            <code>{"{{discount_pct}}"}</code>&nbsp;
            <code>{"{{public_code}}"}</code>
          </p>
        </div>

        {/* Live caption preview — rendered locally from a fixed sample
            post + the user's current FX rate, checkboxes, and custom
            text. Updates as any of those change so the reseller can
            see the final look before saving. */}
        <CaptionPreview
          template={templateFromIncludeAndStyle(include, style)}
          customText={customText}
          fxRate={parseFloat(fxRate || "0")}
        />

        <Btn variant="accent" disabled={saving} style={{ marginTop: 10 }}
             onClick={() => save({
               captionTemplate: templateFromIncludeAndStyle(include, style),
               captionCustomText: customText,
               captionStyle: style,
             }, "Сохранено")}>
          Сохранить
        </Btn>
      </section>

      {/* ── 3. Forward targets ───────────────────────────────────── */}
      <section>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          Каналы для пересылки
        </Mono>
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, marginBottom: 10, lineHeight: 1.5 }}>
          Сначала добавьте <strong style={{ color: T_NEW.ink }}>@Buyergrambot</strong> админом
          в свой Telegram-канал. После этого добавьте сюда канал (или несколько) —
          мы будем отправлять карточки именно туда.
        </p>
        {targets.length === 0 && (
          <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, marginBottom: 8, fontStyle: "italic" }}>
            Каналов пока нет.
          </p>
        )}
        {targets.map((t) => (
          <div key={t.id} style={{
            marginBottom: 12,
            padding: 12,
            background: T_NEW.paperSoft,
            borderRadius: T_NEW.r1,
            border: `1px solid ${T_NEW.ink12}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={t.enabled}
                onChange={(e) => updateTarget(t.id, { enabled: e.target.checked })}
                aria-label="Включён"
                style={{ accentColor: T_NEW.persimmon, width: 16, height: 16 }}
              />
              <Mono color={T_NEW.ink56} size={T_NEW.micro}>
                {t.kind === "telegram" ? "TELEGRAM" : "MAX"}
                {t.kind === "max" && <span style={{ color: T_NEW.persimmon }}> · скоро</span>}
              </Mono>
              <button onClick={() => removeTarget(t.id)} title="Удалить"
                      style={{ marginLeft: "auto", background: "none", border: "none",
                               cursor: "pointer", color: T_NEW.ink56, padding: 4, fontSize: 18 }}>
                ×
              </button>
            </div>
            <div style={{ display: "grid", gap: 8 }}>
              <label style={{ display: "block" }}>
                <Mono color={T_NEW.ink56} size={T_NEW.micro} style={{ display: "block", marginBottom: 4 }}>
                  {t.kind === "telegram"
                    ? "@имя канала или числовой ID"
                    : "MAX ID канала"}
                </Mono>
                <input
                  placeholder={t.kind === "telegram"
                    ? "@my_channel или -1001234567890"
                    : "MAX ID"}
                  value={t.chatId}
                  onChange={(e) => updateTarget(t.id, { chatId: e.target.value })}
                  style={{ ...inputStyle, width: "100%" }}
                />
                {t.kind === "telegram" && (
                  <span style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 4, display: "block" }}>
                    Публичный канал: <code>@my_channel</code>. Приватный: число с минусом
                    (откройте Telegram Web → канал → ⋯ → ID канала).
                  </span>
                )}
              </label>
              {/* Optional friendly nickname — only useful if you have
                  several channels and want to pick them by feel rather
                  than by raw chat ID. Empty by default; the forward
                  picker falls back to the chat ID when this is blank. */}
              <details>
                <summary style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, cursor: "pointer", marginTop: 4 }}>
                  Дополнительно: подпись канала
                </summary>
                <input
                  placeholder="Например: Распродажа обуви"
                  value={t.label}
                  onChange={(e) => updateTarget(t.id, { label: e.target.value })}
                  style={{ ...inputStyle, width: "100%", marginTop: 6 }}
                />
                <span style={{
                  color: T_NEW.ink56, fontSize: T_NEW.micro,
                  marginTop: 4, display: "block",
                }}>
                  Видна только вам в окне пересылки, помогает различить несколько каналов.
                </span>
              </details>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          <Btn variant="outline" onClick={() => addTarget("telegram")} disabled={saving}>
            + Telegram-канал
          </Btn>
          <Btn variant="outline" onClick={() => addTarget("max")} disabled={saving}>
            + MAX (скоро)
          </Btn>
          <Btn variant="accent" disabled={saving}
               onClick={() => save({ forwardTargets: targets }, "Каналы сохранены")}
               style={{ marginLeft: "auto" }}>
            Сохранить каналы
          </Btn>
        </div>
      </section>
    </div>
  );
}


// ─── CustomersTab — reseller's CRM (migration 0044) ─────────────────
// Per-reseller downstream client list. Foundation for Rundler-class
// CRM features. Add / edit / delete + a search box that hits the
// server-side ILIKE filter on name + telegram + phone. Pagination
// via offset; load 100 at a time which is enough for any reseller's
// realistic active list.
//
// Editing is inline-modal style: the same form serves create + edit;
// `editing` carries the row being edited or null for create. Hard
// delete with a confirm prompt — no soft-delete since the data has
// no audit-trail value beyond the reseller's own use.
export function CustomersTab({ toast }) {
  const [rows, setRows] = useState(null);  // null = initial load
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // null | "new" | row
  // Customer being viewed in the detail modal. null = no detail open.
  // Distinct state from `editing` so a user can open the detail view,
  // tap "Изменить" from inside it, and we cleanly transition to the
  // form modal without leaking either state.
  const [detailOpen, setDetailOpen] = useState(null);
  // Bulk-messaging selection mode. When `selectMode` is true each
  // CustomerRow renders a checkbox instead of being click-to-open.
  // selectedIds is a Set of CRM customer IDs the user has ticked.
  // bulkOpen flips on when the user clicks "Написать всем".
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const reload = async (q = query) => {
    setLoading(true);
    try {
      const list = await api.listMyCustomers({ q: q.trim(), limit: 200 });
      setRows(list || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(""); /* initial load */ }, []);

  // Debounced search — wait 350ms after typing stops before hitting
  // the server. Avoids a request per keystroke for resellers with
  // big lists.
  useEffect(() => {
    const t = setTimeout(() => { reload(query); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const onDelete = async (customer) => {
    if (!await toast.confirm(`Удалить клиента «${customer.name}»?`)) return;
    try {
      await api.deleteMyCustomer(customer.id);
      await reload();
      toast.success("Клиент удалён");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div style={{ padding: "8px 0" }}>
      {/* Toolbar: search + Add button */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14,
                    alignItems: "center", flexWrap: "wrap" }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Поиск по имени, Telegram или телефону"
          style={{
            flex: 1, minWidth: 200,
            padding: "8px 12px",
            border: `1px solid ${T_NEW.ink12}`,
            borderRadius: T_NEW.r1,
            fontFamily: T_NEW.fontSans,
            fontSize: T_NEW.body,
            background: T_NEW.paperRaised,
            color: T_NEW.ink,
          }}
        />
        <Btn variant="accent" onClick={() => setEditing("new")}>
          + Добавить
        </Btn>
        {!selectMode ? (
          <Btn variant="ghost"
               onClick={() => setSelectMode(true)}
               disabled={!rows?.length}>
            Выбрать
          </Btn>
        ) : (
          <Btn variant="ghost" onClick={exitSelectMode}>
            Отмена
          </Btn>
        )}
      </div>

      {loading && rows === null && (
        <p style={{ color: T_NEW.ink56, padding: 16 }}>Загрузка…</p>
      )}

      {rows !== null && rows.length === 0 && (
        <div style={{ padding: "24px 12px", textAlign: "center" }}>
          <p style={{ fontSize: T_NEW.body, color: T_NEW.ink, marginBottom: 4 }}>
            {query ? "Никого не нашлось" : "Пока нет клиентов."}
          </p>
          {!query && (
            <p style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, lineHeight: 1.5 }}>
              Добавьте клиента — будем хранить контакты, заметки и историю заказов.
            </p>
          )}
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <div style={{ display: "grid", gap: 6,
                      paddingBottom: selectMode ? 70 : 0 }}>
          {rows.map((c) => (
            <CustomerRow key={c.id}
                         customer={c}
                         selectMode={selectMode}
                         selected={selectedIds.has(c.id)}
                         onToggleSelect={() => toggleSelected(c.id)}
                         onOpen={() => setDetailOpen(c)}
                         onEdit={() => setEditing(c)}
                         onDelete={() => onDelete(c)} />
          ))}
        </div>
      )}

      {/* Sticky action bar — appears in selection mode. Counts the
          ticked customers and exposes the "Написать всем" CTA. The
          modal it opens generates one t.me deeplink per recipient
          who has a Telegram handle. */}
      {selectMode && (
        <div style={{
          position: "sticky", bottom: 0,
          padding: "10px 12px",
          background: T_NEW.paperRaised,
          borderTop: `1px solid ${T_NEW.ink12}`,
          display: "flex", alignItems: "center", gap: 10,
          marginTop: 8,
          boxShadow: "0 -2px 8px rgba(20,17,13,0.04)",
        }}>
          <span style={{ fontSize: T_NEW.meta, color: T_NEW.ink56 }}>
            Выбрано: <strong style={{ color: T_NEW.ink }}>
              {selectedIds.size}
            </strong>
          </span>
          <div style={{ flex: 1 }} />
          <Btn variant="accent"
               disabled={selectedIds.size === 0}
               onClick={() => setBulkOpen(true)}>
            Написать всем
          </Btn>
        </div>
      )}

      {bulkOpen && (
        <BulkMessageModal
          customers={(rows || []).filter((c) => selectedIds.has(c.id))}
          onClose={() => setBulkOpen(false)}
          toast={toast}
        />
      )}

      {editing && (
        <CustomerFormModal
          customer={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
          toast={toast}
        />
      )}

      {detailOpen && (
        <CustomerDetailModal
          customer={detailOpen}
          onClose={() => setDetailOpen(null)}
          onEdit={() => { setDetailOpen(null); setEditing(detailOpen); }}
          onDelete={async () => {
            const c = detailOpen;
            setDetailOpen(null);
            await onDelete(c);
          }}
          // Fired after an inline edit (notes) saves successfully.
          // Background-reload the list so CustomerRow's preview
          // snippet picks up the new text without forcing the user
          // to close the modal.
          onSaved={() => reload()}
          toast={toast}
        />
      )}
    </div>
  );
}

function CustomerRow({ customer, onOpen, onEdit, onDelete,
                       selectMode = false, selected = false,
                       onToggleSelect }) {
  const totalSpent = (customer.totalSpentCents ?? 0) / 100;
  const lastActivity = customer.lastActivityAt
    ? new Date(customer.lastActivityAt * 1000)
    : null;
  const hasOrders = (customer.totalOrders ?? 0) > 0;
  // In select mode, the row click toggles selection instead of
  // opening the detail modal. Customers without a Telegram handle
  // are visually de-emphasised but still selectable — the bulk
  // composer surfaces them as "(no handle)".

  return (
    <div
      onClick={selectMode ? onToggleSelect : onOpen}
      style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "10px 12px", borderRadius: T_NEW.r1,
        boxShadow: selected ? `inset 0 0 0 2px ${T_NEW.persimmon}` : "none",
        background: T_NEW.paperSoft,
        border: `1px solid ${T_NEW.ink12}`,
        cursor: "pointer",
      }}>
      {selectMode && (
        <div style={{
          width: 22, height: 22, flexShrink: 0,
          borderRadius: T_NEW.r1,
          border: `1.5px solid ${selected
            ? T_NEW.persimmon : T_NEW.ink38}`,
          background: selected ? T_NEW.persimmon : "transparent",
          color: "#fff", display: "flex",
          alignItems: "center", justifyContent: "center",
          fontSize: 14, fontWeight: 700, lineHeight: 1,
        }}>
          {selected ? "✓" : ""}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.body, color: T_NEW.ink,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis" }}>
          {customer.name}
          {customer.category && (
            <span style={{
              marginLeft: 8, fontSize: T_NEW.micro,
              color: T_NEW.persimmon, fontFamily: T_NEW.fontMono,
              textTransform: "uppercase", letterSpacing: T_NEW.monoTrack,
              fontWeight: 700,
            }}>
              {customer.category}
            </span>
          )}
        </div>
        <Mono color={T_NEW.ink56} size={T_NEW.micro}>
          {[customer.telegramHandle, customer.phone].filter(Boolean).join(" · ")
            || "—"}
        </Mono>
        {hasOrders && (
          <Mono color={T_NEW.ink} size={T_NEW.micro}
                style={{ display: "block", marginTop: 4 }}>
            {customer.totalOrders} заказ
            {customer.totalOrders === 1 ? "" :
              (customer.totalOrders < 5 ? "а" : "ов")}
            {totalSpent > 0 && ` · $${totalSpent.toFixed(2)}`}
            {lastActivity &&
              ` · последний ${lastActivity.toLocaleDateString("ru-RU")}`}
          </Mono>
        )}
        {customer.notes && (
          <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56,
                        marginTop: 4, lineHeight: 1.4,
                        display: "-webkit-box", WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {customer.notes}
          </div>
        )}
      </div>
      {!selectMode && (
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}
             onClick={(e) => e.stopPropagation()}>
          <button onClick={onEdit}
                  style={{ background: "transparent", color: T_NEW.ink56,
                           border: `1px solid ${T_NEW.ink12}`,
                           padding: "6px 10px", borderRadius: T_NEW.r1,
                           cursor: "pointer", fontSize: T_NEW.micro }}>
            Изменить
          </button>
          <button onClick={onDelete}
                  style={{ background: "transparent", color: "#a33",
                           border: `1px solid #a33${"33"}`,
                           padding: "6px 10px", borderRadius: T_NEW.r1,
                           cursor: "pointer", fontSize: T_NEW.micro }}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CustomerDetailModal ──────────────────────────────────────────
// Full read view for a single customer. Pulls order history from
// /api/me/customers/:id/orders. The customer object passed in already
// carries live totals (totalOrders, totalSpentCents, lastActivityAt)
// from the list query, so we don't refetch the basic info — we just
// render what we have plus the order timeline.
//
// Edit / Delete buttons close this modal and hand off to the
// CustomersTab so there's never two modals stacked. Click outside or
// press × to dismiss.
// BulkMessageModal — composes one message and renders N
// `https://t.me/<handle>?text=<encoded>` deeplinks the reseller can
// open and send manually. No backend send for v1 — Telegram's
// anti-spam rules require each customer to have started the bot first,
// which we don't have an onboarding flow for yet. This composer
// nonetheless saves resellers from re-typing the same message N times.
//
// Template vars resolved per-recipient:
//   {имя}     — customer.name (or empty)
//   {handle}  — telegram_handle without the leading @
function renderBulkVars(template, customer) {
  const firstName = (customer.name || "").trim().split(/\s+/)[0] || "";
  const handle = (customer.telegramHandle || "").replace(/^@/, "");
  return template
    .replace(/\{имя\}/g, firstName)
    .replace(/\{handle\}/g, handle);
}
function bulkDeeplink(handle, text) {
  const h = (handle || "").replace(/^@/, "").trim();
  if (!h) return null;
  return `https://t.me/${encodeURIComponent(h)}?text=${encodeURIComponent(text)}`;
}

function BulkMessageModal({ customers, onClose, toast }) {
  const t = useT();
  const [text, setText] = useState(t("bulk.template.default"));
  // Per-customer bot-link status: undefined = still loading, true =
  // linked, false = not linked. Resolved by checking the bot-link
  // endpoint for each selected customer on mount; small lookup cost,
  // tiny payload.
  const [linked, setLinked] = useState({});
  const [sending, setSending] = useState(false);
  const [sendReport, setSendReport] = useState(null);  // null | { sent, noLink, failed }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(customers.map(async (c) => {
        try {
          const r = await api.getCustomerBotLink(c.id);
          out[c.id] = !!(r && r.linked);
        } catch { out[c.id] = false; }
      }));
      if (!cancelled) setLinked(out);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reachableHandle = customers.filter(
    (c) => (c.telegramHandle || "").trim(),
  );
  const linkedCustomers = customers.filter((c) => linked[c.id]);
  const unlinkedWithHandle = reachableHandle.filter((c) => !linked[c.id]);
  const unreachable =
    customers.length - linkedCustomers.length - unlinkedWithHandle.length;

  const { ref: sheetRef, dy, dismissing } = useSwipeDownDismiss(onClose);
  const dragging = dy > 0;
  const scrim = dragging ? Math.max(0.05, 0.5 - dy / 900) : 0.5;

  const openAll = () => {
    // Browsers block more than a few window.opens in a single tick.
    // Spread them slightly so the first few open and the user can
    // confirm the rest one at a time via the per-row buttons.
    unlinkedWithHandle.slice(0, 5).forEach((c, i) => {
      const url = bulkDeeplink(c.telegramHandle, renderBulkVars(text, c));
      if (url) setTimeout(() => window.open(url, "_blank"), i * 80);
    });
    if (unlinkedWithHandle.length > 5) {
      toast?.info?.(t("bulk.openSomeToast", { n: unlinkedWithHandle.length }));
    }
  };

  const sendViaBot = async () => {
    if (linkedCustomers.length === 0) return;
    setSending(true);
    setSendReport(null);
    try {
      const r = await api.bulkMessageCustomers({
        customerIds: linkedCustomers.map((c) => c.id),
        template: text,
      });
      setSendReport({
        sent: r.sent || 0,
        noLink: r.noLink || 0,
        failed: r.failed || 0,
      });
      if (r.sent > 0) {
        toast?.success?.(t("bulk.sentToast", { n: r.sent }));
      }
      if (r.failed > 0) {
        toast?.error?.(t("bulk.failedToast", { n: r.failed }));
      }
    } catch (e) {
      toast?.error?.(e.message || t("bulk.errorToast"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         className="cg-modal-bg"
         style={{
           position: "fixed", inset: 0,
           background: `rgba(20,17,13,${scrim})`,
           transition: dragging ? "none" : "background-color 180ms",
           pointerEvents: dismissing ? "none" : "auto",
           display: "flex", alignItems: "center", justifyContent: "center",
           zIndex: 100, padding: 20,
         }}>
      <div ref={sheetRef} className="cg-modal-body"
           style={{
             background: T_NEW.paperRaised,
             borderRadius: T_NEW.r3, padding: 20,
             maxWidth: 560, width: "100%",
             maxHeight: "92vh", overflowY: "auto",
             boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
             transform: `translateY(${dy}px)`,
             transition: dragging ? "none"
                                  : "transform 220ms cubic-bezier(0.2,0,0.0,1.0)",
           }}>
        <div className="cg-modal-grab" aria-hidden />
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 12 }}>
          <Serif size={T_NEW.displaySm}>{t("bulk.title")}</Serif>
          <button onClick={onClose} aria-label={t("bulk.close")}
                  style={{ background: "none", border: "none",
                           cursor: "pointer", color: T_NEW.ink56,
                           fontSize: 22, lineHeight: 1, padding: 4 }}>
            ×
          </button>
        </div>

        <Mono color={T_NEW.ink56}
              style={{ display: "block", marginBottom: 6 }}>
          {t("bulk.template")}
        </Mono>
        <textarea value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={4}
                  placeholder={t("bulk.template.placeholder")}
                  style={{ width: "100%", padding: "10px 12px",
                           background: T_NEW.paperSoft,
                           border: `1px solid ${T_NEW.ink12}`,
                           borderRadius: T_NEW.r1,
                           fontSize: T_NEW.meta, color: T_NEW.ink,
                           lineHeight: 1.5, fontFamily: T_NEW.fontSans,
                           boxSizing: "border-box", outline: "none",
                           resize: "vertical" }} />
        <Mono color={T_NEW.ink38} size={T_NEW.micro}
              style={{ display: "block", marginTop: 6,
                       marginBottom: 16 }}>
          {t("bulk.template.vars", { var1: "{имя}", var2: "{handle}" })}
        </Mono>

        {/* Recipient split summary — three groups: linked to bot
            (one-click broadcast), with handle but no bot link
            (manual deeplink), no handle at all (skipped). */}
        <div style={{
          padding: "10px 12px", marginBottom: 12,
          background: T_NEW.paperSoft,
          border: `1px solid ${T_NEW.ink12}`,
          borderRadius: T_NEW.r1, fontSize: T_NEW.meta,
          color: T_NEW.ink, lineHeight: 1.6,
        }}>
          <div>
            <strong style={{ color: T_NEW.bottle }}>
              {linkedCustomers.length}
            </strong>{" "}{t("bulk.summary.bot")}{" "}
            {sendReport && (
              <Mono color={T_NEW.bottle} size={T_NEW.micro}>
                {t("bulk.summary.bot.sentOf", { sent: sendReport.sent, total: linkedCustomers.length })}
              </Mono>
            )}
          </div>
          <div>
            <strong style={{ color: T_NEW.persimmon }}>
              {unlinkedWithHandle.length}
            </strong>{" "}{t("bulk.summary.manual")}
          </div>
          {unreachable > 0 && (
            <div style={{ color: T_NEW.ink56 }}>
              {unreachable} {t("bulk.summary.skip")}
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 16,
                      flexWrap: "wrap" }}>
          {linkedCustomers.length > 0 && (
            <Btn variant="accent" onClick={sendViaBot}
                 disabled={sending || sendReport?.sent > 0}>
              {sending ? t("bulk.sending")
                : sendReport?.sent > 0
                  ? t("bulk.sent", { n: sendReport.sent })
                  : t("bulk.sendViaBot", { n: linkedCustomers.length })}
            </Btn>
          )}
          {unlinkedWithHandle.length > 0 && (
            <Btn variant={linkedCustomers.length > 0 ? "ghost" : "accent"}
                 onClick={openAll}>
              {t("bulk.openFirst5")}
            </Btn>
          )}
          <Btn variant="ghost" onClick={onClose}>{t("bulk.close")}</Btn>
        </div>

        {unlinkedWithHandle.length > 0 && (
          <Mono color={T_NEW.ink56}
                style={{ display: "block", marginBottom: 8 }}>
            {t("bulk.list")}
          </Mono>
        )}
        <div style={{ display: "grid", gap: 6 }}>
          {unlinkedWithHandle.map((c) => {
            const preview = renderBulkVars(text, c);
            const url = bulkDeeplink(c.telegramHandle, preview);
            return (
              <div key={c.id} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "8px 10px",
                background: T_NEW.paperSoft,
                border: `1px solid ${T_NEW.ink12}`,
                borderRadius: T_NEW.r1,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                                fontWeight: 600 }}>
                    {c.name}
                  </div>
                  <Mono color={T_NEW.ink56} size={T_NEW.micro}>
                    {c.telegramHandle}
                  </Mono>
                </div>
                <a href={url} target="_blank" rel="noopener noreferrer"
                   style={{
                     background: T_NEW.persimmon, color: "#fff",
                     textDecoration: "none",
                     padding: "6px 12px", borderRadius: T_NEW.r1,
                     fontSize: T_NEW.micro, fontWeight: 700,
                     fontFamily: T_NEW.fontMono,
                     textTransform: "uppercase",
                     letterSpacing: T_NEW.monoTrack,
                   }}>
                  {t("bulk.open")}
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(preview);
                    toast?.success?.(t("bulk.copied"));
                  }}
                  title={t("bulk.copy.title")}
                  style={{
                    background: "transparent", color: T_NEW.ink56,
                    border: `1px solid ${T_NEW.ink12}`,
                    padding: "6px 10px", borderRadius: T_NEW.r1,
                    fontSize: T_NEW.micro, cursor: "pointer",
                    fontFamily: T_NEW.fontSans,
                  }}>{t("bulk.copy")}</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CustomerDetailModal({ customer, onClose, onEdit, onDelete, onSaved, toast }) {
  const t = useT();
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(true);
  // Reorder flow: when the user clicks "Снова" on a timeline row we
  // fetch the post fresh and stash both the post and the original
  // order. OrderModal is then rendered with previousUnitPriceCents so
  // it can show a strike-through if the price changed.
  const [reorderPost, setReorderPost] = useState(null);
  const [reorderOrder, setReorderOrder] = useState(null);
  const [reorderingId, setReorderingId] = useState(null);
  // Inline notes editing. notesValue is the displayed string (kept
  // locally so we don't have to round-trip through the parent's
  // detailOpen state — which holds a list-shaped row with live
  // aggregate columns we don't want to overwrite with a PATCH
  // response). notesDraft is the textarea buffer.
  const [notesValue, setNotesValue] = useState(customer.notes || "");
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState(customer.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  // Reminder state — list of open reminders for this customer (we
  // only render the soonest one in the header; the rest are visible
  // in the history). composing toggles the inline composer.
  const [reminders, setReminders] = useState([]);
  const [composingReminder, setComposingReminder] = useState(false);
  const [reminderDays, setReminderDays] = useState(7);
  const [reminderNote, setReminderNote] = useState("");
  const [savingReminder, setSavingReminder] = useState(false);
  // Internal tab — Контакты / История / Заметки. The modal grew
  // tall enough that vertical scrolling buried the order timeline
  // and notes behind contact + reminders. Tabs make each surface
  // dedicated. Default to "contact" so the first thing a reseller
  // sees is who and how to reach them.
  const [detailTab, setDetailTab] = useState("contact");

  // Re-sync local state if the parent swaps in a different customer
  // (e.g. user closes and re-opens the modal on a different row).
  useEffect(() => {
    setNotesValue(customer.notes || "");
    setNotesDraft(customer.notes || "");
    setEditingNotes(false);
    setComposingReminder(false);
    setReminderDays(7);
    setReminderNote("");
  }, [customer.id]);

  const loadReminders = async () => {
    try {
      const list = await api.listCustomerReminders(customer.id);
      setReminders(list || []);
    } catch (e) {
      toast?.error?.(e.message);
    }
  };
  useEffect(() => {
    loadReminders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id]);

  const activeReminder = reminders.find((r) => !r.completedAt) || null;

  const createReminder = async () => {
    const days = Math.max(1, Math.min(365, parseInt(reminderDays, 10) || 0));
    const remindAt = Math.floor(Date.now() / 1000) + days * 86400;
    setSavingReminder(true);
    try {
      await api.createCustomerReminder(customer.id, {
        remindAt,
        note: reminderNote.trim(),
      });
      setComposingReminder(false);
      setReminderNote("");
      setReminderDays(7);
      await loadReminders();
      toast?.success?.(t("customer.detail.reminder.createdToast"));
    } catch (e) {
      toast?.error?.(e.message);
    } finally {
      setSavingReminder(false);
    }
  };

  const markReminderDone = async (r) => {
    try {
      await api.updateReminder(r.id, { completed: true });
      await loadReminders();
    } catch (e) {
      toast?.error?.(e.message);
    }
  };
  const deleteReminderRow = async (r) => {
    try {
      await api.deleteReminder(r.id);
      await loadReminders();
    } catch (e) {
      toast?.error?.(e.message);
    }
  };

  const beginEditNotes = () => {
    setNotesDraft(notesValue);
    setEditingNotes(true);
  };
  const cancelEditNotes = () => {
    setEditingNotes(false);
    setNotesDraft(notesValue);
  };
  const saveNotes = async () => {
    const trimmed = notesDraft.trim();
    if (trimmed === notesValue.trim()) {
      // Nothing changed — just close the editor without hitting the
      // network. Saves a round-trip when the user hits Save on an
      // unmodified textarea.
      setEditingNotes(false);
      return;
    }
    setSavingNotes(true);
    try {
      const updated = await api.updateMyCustomer(customer.id, {
        notes: trimmed,
      });
      setNotesValue(updated?.notes || "");
      setEditingNotes(false);
      toast?.success?.(t("customer.detail.notes.savedToast"));
      // Tell the parent to reload its list so the CustomerRow preview
      // shows the new notes snippet. We do NOT push the PATCH response
      // up as the new detailOpen — the list-row shape has live
      // aggregate columns (totalOrders/totalSpent/lastActivity) that
      // the PATCH endpoint doesn't compute, and replacing the prop
      // would zero them out in this modal's stats trio.
      onSaved?.();
    } catch (e) {
      toast?.error?.(e.message);
    } finally {
      setSavingNotes(false);
    }
  };
  const notesKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEditNotes();
    } else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      saveNotes();
    }
  };

  const loadOrders = async () => {
    try {
      const list = await api.listMyCustomerOrders(customer.id);
      setOrders(list || []);
    } catch (e) {
      toast?.error?.(e.message);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listMyCustomerOrders(customer.id);
        if (!cancelled) setOrders(list || []);
      } catch (e) {
        if (!cancelled) toast?.error?.(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customer.id]);

  const startReorder = async (order) => {
    setReorderingId(order.id);
    try {
      const post = await api.getPost(order.postId);
      // Defensive: server may return the post even when not currently
      // viewable. Treat anything that isn't a published post as gone.
      if (!post || (post.status && post.status !== "published")) {
        toast?.error?.(t("customer.detail.reorderUnavailable"));
        return;
      }
      setReorderPost(post);
      setReorderOrder(order);
    } catch (e) {
      // 404 / network — same UX: tell the user the SKU is gone.
      toast?.error?.(t("customer.detail.reorderUnavailable"));
    } finally {
      setReorderingId(null);
    }
  };

  const closeReorder = () => {
    setReorderPost(null);
    setReorderOrder(null);
  };

  const onReorderDone = async () => {
    closeReorder();
    // Refresh the timeline so the brand-new order shows up at the top.
    await loadOrders();
  };

  const totalSpent = (customer.totalSpentCents ?? 0) / 100;
  const lastActivity = customer.lastActivityAt
    ? new Date(customer.lastActivityAt * 1000)
    : null;
  const created = customer.createdAt
    ? new Date(customer.createdAt * 1000)
    : null;

  // Bottom-sheet swipe-down on mobile. Same hook the shared <Modal>
  // uses; this sub-modal hand-rolls its own scrim/card layout (so it
  // can sit on top of SettingsModal without a layering fight) but it
  // gets the gesture for free.
  const { ref: sheetRef, dy: sheetDy, dismissing: sheetClosing } =
    useSwipeDownDismiss(onClose);
  const sheetDragging = sheetDy > 0;
  const sheetScrim = sheetDragging
    ? Math.max(0.05, 0.5 - sheetDy / 900) : 0.5;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="cg-modal-bg"
      style={{
        position: "fixed", inset: 0,
        background: `rgba(20, 17, 13, ${sheetScrim})`,
        transition: sheetDragging ? "none" : "background-color 180ms",
        pointerEvents: sheetClosing ? "none" : "auto",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}>
      <div ref={sheetRef} className="cg-modal-body"
           style={{
        background: T_NEW.paperRaised,
        borderRadius: T_NEW.r3,
        padding: 20,
        maxWidth: 560, width: "100%",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        transform: `translateY(${sheetDy}px)`,
        transition: sheetDragging ? "none"
                                   : "transform 220ms cubic-bezier(0.2,0,0.0,1.0)",
      }}>
        <div className="cg-modal-grab" aria-hidden />
        {/* Header — name + category badge + close button */}
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", marginBottom: 14, gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Serif size={T_NEW.displaySm}
                   style={{ fontWeight: 500, lineHeight: 1.2 }}>
              {customer.name}
            </Serif>
            {customer.category && (
              <Mono color={T_NEW.persimmon}
                    style={{ display: "block", marginTop: 6,
                             fontWeight: 700 }}>
                {customer.category.toUpperCase()}
              </Mono>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
                  style={{ background: "none", border: "none", cursor: "pointer",
                           color: T_NEW.ink56, fontSize: 24, lineHeight: 1,
                           padding: 4, flexShrink: 0 }}>
            ×
          </button>
        </div>

        {/* Tab strip — three internal sections so the modal doesn't
            become a scroll wall on phones. */}
        <div style={{ display: "flex", gap: 2, marginBottom: 16,
                      borderBottom: `1px solid ${T_NEW.ink12}` }}>
          {[
            { id: "contact",   label: t("customer.detail.tab.contact") },
            // Addresses tab (migration 0054). Lives between Контакты
            // and История because the address picker is most relevant
            // right before placing an order.
            { id: "addresses", label: t("customer.addresses.title") },
            { id: "history",   label: t("customer.detail.tab.history") },
            { id: "notes",     label: t("customer.detail.tab.notes")  },
          ].map((tt) => {
            const active = tt.id === detailTab;
            return (
              <button key={tt.id}
                      onClick={() => setDetailTab(tt.id)}
                      style={{
                        flex: 1, padding: "10px 12px",
                        background: "transparent", border: "none",
                        borderBottom: active
                          ? `2px solid ${T_NEW.persimmon}`
                          : `2px solid transparent`,
                        marginBottom: -1,
                        cursor: "pointer",
                        fontFamily: T_NEW.fontMono,
                        fontSize: T_NEW.micro,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: T_NEW.monoTrack,
                        color: active ? T_NEW.persimmon : T_NEW.ink56,
                      }}>
                {tt.label}
              </button>
            );
          })}
        </div>

        {/* Contact block */}
        {detailTab === "contact" && (
        <>
        {/* contact-tab-start */}
        <div style={{ marginBottom: 16 }}>
          <Mono color={T_NEW.ink56}
                style={{ display: "block", marginBottom: 8 }}>
            {t("customer.detail.contacts")}
          </Mono>
          <div style={{ display: "grid", gap: 6, fontSize: T_NEW.meta,
                        color: T_NEW.ink, lineHeight: 1.5 }}>
            {customer.telegramHandle ? (
              <div>
                <span style={{ color: T_NEW.ink56, marginRight: 8 }}>{t("customer.detail.tg.label")}</span>
                <a href={`https://t.me/${customer.telegramHandle.replace(/^@/, "")}`}
                   target="_blank" rel="noopener noreferrer"
                   style={{ color: T_NEW.persimmon, textDecoration: "none" }}>
                  {customer.telegramHandle}
                </a>
              </div>
            ) : (
              <div style={{ color: T_NEW.ink38 }}>{t("customer.detail.tg.none")}</div>
            )}
            {customer.phone ? (
              <div>
                <span style={{ color: T_NEW.ink56, marginRight: 8 }}>{t("customer.detail.phone.label")}</span>
                <a href={`tel:${customer.phone}`}
                   style={{ color: T_NEW.persimmon, textDecoration: "none" }}>
                  {customer.phone}
                </a>
              </div>
            ) : (
              <div style={{ color: T_NEW.ink38 }}>{t("customer.detail.phone.none")}</div>
            )}
            {created && (
              <div style={{ color: T_NEW.ink56, fontSize: T_NEW.micro,
                            marginTop: 2 }}>
                {t("customer.detail.inBaseSince", { date: created.toLocaleDateString("ru-RU") })}
              </div>
            )}
          </div>
          {/* Customer-bot invite — generates a t.me/<bot>?start=<sig>
              deeplink the reseller shares with the customer. When the
              customer hits /start, the bot calls back to
              /api/admin/telegram/customer-bot/start and the link
              shows "Подключён к боту". */}
          <CustomerBotInviteRow customerId={customer.id} toast={toast} t={t} />
        </div>

        {/* Stats trio — orders, total spent, last activity */}
        <div style={{ display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr",
                      gap: 8, marginBottom: 16 }}>
          <DetailStat label={t("customer.detail.stats.orders")} value={customer.totalOrders ?? 0} />
          <DetailStat label={t("customer.detail.stats.spent")} value={`$${totalSpent.toFixed(2)}`} />
          <DetailStat label={t("customer.detail.stats.last")}
                      value={lastActivity
                        ? lastActivity.toLocaleDateString("ru-RU", {
                            day: "2-digit", month: "2-digit",
                          })
                        : "—"} />
        </div>

        </>
        )}

        {/* Addresses tab (migration 0054). CRUD over
            /api/me/customers/:cid/addresses — same shape as the
            reseller's own book but with is_primary instead of
            is_default. Rendered as its own self-contained widget
            so the tab logic stays simple. */}
        {detailTab === "addresses" && (
          <CustomerAddressesPanel customerId={customer.id} toast={toast} />
        )}

        {/* Notes tab — inline editable. Click the body or
            "+ Добавить заметку" to enter edit mode. Saves via PATCH
            /api/me/customers/:id; ⌘↵ to save, Esc to cancel. */}
        {detailTab === "notes" && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginBottom: 6 }}>
            <Mono color={T_NEW.ink56}>{t("customer.detail.notes")}</Mono>
            {!editingNotes && notesValue && (
              <button
                type="button"
                onClick={beginEditNotes}
                style={{
                  background: "transparent",
                  color: T_NEW.persimmon,
                  border: "none",
                  padding: 0,
                  fontFamily: T_NEW.fontMono,
                  fontSize: T_NEW.micro,
                  letterSpacing: T_NEW.monoTrack,
                  textTransform: "uppercase",
                  fontWeight: 600,
                  cursor: "pointer",
                }}>
                {t("customer.detail.notes.edit")}
              </button>
            )}
          </div>
          {editingNotes ? (
            <div>
              <textarea
                autoFocus
                value={notesDraft}
                onChange={(e) => setNotesDraft(e.target.value)}
                onKeyDown={notesKeyDown}
                maxLength={2000}
                placeholder={t("customer.detail.notes.placeholder")}
                rows={5}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  background: T_NEW.paperRaised,
                  border: `1px solid ${T_NEW.ink12}`,
                  borderRadius: T_NEW.r1,
                  fontSize: T_NEW.meta,
                  color: T_NEW.ink,
                  lineHeight: 1.55,
                  fontFamily: T_NEW.fontSans,
                  resize: "vertical",
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
              <div style={{ display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 6, gap: 8 }}>
                <Mono color={T_NEW.ink38} size={T_NEW.micro}>
                  {t("customer.detail.notes.help", { n: notesDraft.length })}
                </Mono>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={cancelEditNotes}
                    disabled={savingNotes}
                    style={{
                      background: "transparent", color: T_NEW.ink56,
                      border: `1px solid ${T_NEW.ink12}`,
                      padding: "6px 12px", borderRadius: T_NEW.r1,
                      cursor: "pointer", fontSize: T_NEW.micro,
                      fontFamily: T_NEW.fontSans,
                    }}>
                    {t("customer.detail.notes.cancel")}
                  </button>
                  <button
                    onClick={saveNotes}
                    disabled={savingNotes}
                    style={{
                      background: T_NEW.persimmon, color: "#fff",
                      border: "none",
                      padding: "6px 12px", borderRadius: T_NEW.r1,
                      cursor: savingNotes ? "wait" : "pointer",
                      fontSize: T_NEW.micro, fontWeight: 600,
                      fontFamily: T_NEW.fontSans,
                      opacity: savingNotes ? 0.7 : 1,
                    }}>
                    {savingNotes ? t("customer.detail.notes.saving") : t("customer.detail.notes.save")}
                  </button>
                </div>
              </div>
            </div>
          ) : notesValue ? (
            <div
              onClick={beginEditNotes}
              title={t("customer.detail.notes.clickToEdit")}
              style={{
                padding: "10px 12px",
                background: T_NEW.paperSoft,
                border: `1px solid ${T_NEW.ink12}`,
                borderRadius: T_NEW.r1,
                fontSize: T_NEW.meta,
                color: T_NEW.ink,
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                cursor: "text",
              }}>
              {notesValue}
            </div>
          ) : (
            <button
              type="button"
              onClick={beginEditNotes}
              style={{
                background: "transparent",
                border: `1px dashed ${T_NEW.ink12}`,
                borderRadius: T_NEW.r1,
                padding: "10px 12px",
                color: T_NEW.ink56,
                fontSize: T_NEW.meta,
                fontStyle: "italic",
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                fontFamily: T_NEW.fontSans,
              }}>
              {t("customer.detail.notes.add")}
            </button>
          )}
        </div>
        )}

        {/* Reminders — back in the contact tab. The "напоминание"
            concept is per-customer-contact, not per-note, so this
            sits with phone/Telegram/stats rather than with the
            text-heavy Заметки. */}
        {detailTab === "contact" && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginBottom: 6 }}>
            <Mono color={T_NEW.ink56}>{t("customer.detail.reminders")}</Mono>
            {!composingReminder && (
              <button
                type="button"
                onClick={() => setComposingReminder(true)}
                style={{
                  background: "transparent", color: T_NEW.persimmon,
                  border: "none", padding: 0,
                  fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
                  letterSpacing: T_NEW.monoTrack,
                  textTransform: "uppercase",
                  fontWeight: 600, cursor: "pointer",
                }}>
                {activeReminder ? t("customer.detail.reminders.edit") : t("customer.detail.reminders.add")}
              </button>
            )}
          </div>
          {composingReminder ? (
            <div style={{
              padding: "10px 12px",
              background: T_NEW.paperSoft,
              border: `1px solid ${T_NEW.ink12}`,
              borderRadius: T_NEW.r1,
            }}>
              <div style={{ display: "flex", gap: 8,
                            alignItems: "center", marginBottom: 8,
                            flexWrap: "wrap" }}>
                <span style={{ fontSize: T_NEW.meta,
                               color: T_NEW.ink56 }}>
                  {t("customer.detail.reminder.in")}
                </span>
                <input type="number" min={1} max={365}
                       value={reminderDays}
                       onChange={(e) =>
                         setReminderDays(e.target.value)}
                       style={{ width: 60, padding: "4px 6px",
                                background: T_NEW.paperRaised,
                                border: `1px solid ${T_NEW.ink12}`,
                                borderRadius: T_NEW.r1,
                                fontSize: T_NEW.meta,
                                color: T_NEW.ink,
                                fontFamily: T_NEW.fontMono,
                                textAlign: "center" }} />
                <span style={{ fontSize: T_NEW.meta,
                               color: T_NEW.ink56 }}>{t("customer.detail.reminder.days")}</span>
              </div>
              <input type="text" placeholder={t("customer.detail.reminder.note.placeholder")}
                     value={reminderNote}
                     onChange={(e) => setReminderNote(e.target.value)}
                     maxLength={500}
                     style={{ width: "100%", padding: "8px 10px",
                              background: T_NEW.paperRaised,
                              border: `1px solid ${T_NEW.ink12}`,
                              borderRadius: T_NEW.r1,
                              fontSize: T_NEW.meta, color: T_NEW.ink,
                              fontFamily: T_NEW.fontSans,
                              boxSizing: "border-box", outline: "none",
                              marginBottom: 8 }} />
              <div style={{ display: "flex", justifyContent: "flex-end",
                            gap: 6 }}>
                <button onClick={() => setComposingReminder(false)}
                        disabled={savingReminder}
                        style={{
                          background: "transparent",
                          color: T_NEW.ink56,
                          border: `1px solid ${T_NEW.ink12}`,
                          padding: "6px 12px",
                          borderRadius: T_NEW.r1,
                          cursor: "pointer",
                          fontSize: T_NEW.micro,
                          fontFamily: T_NEW.fontSans,
                        }}>{t("customer.detail.reminder.cancel")}</button>
                <button onClick={createReminder}
                        disabled={savingReminder}
                        style={{
                          background: T_NEW.persimmon, color: "#fff",
                          border: "none",
                          padding: "6px 12px",
                          borderRadius: T_NEW.r1,
                          cursor: savingReminder ? "wait" : "pointer",
                          fontSize: T_NEW.micro, fontWeight: 600,
                          fontFamily: T_NEW.fontSans,
                          opacity: savingReminder ? 0.7 : 1,
                        }}>
                  {savingReminder ? t("customer.detail.reminder.saving") : t("customer.detail.reminder.create")}
                </button>
              </div>
            </div>
          ) : activeReminder ? (
            <ReminderRow r={activeReminder}
                         onDone={() => markReminderDone(activeReminder)}
                         onDelete={() => deleteReminderRow(activeReminder)}
                         t={t} />
          ) : (
            <p style={{ color: T_NEW.ink38, fontSize: T_NEW.meta,
                        fontStyle: "italic", margin: 0 }}>
              {t("customer.detail.reminders.empty")}
            </p>
          )}
        </div>
        )}

        {/* Order timeline — history tab. */}
        {detailTab === "history" && (
        <div style={{ marginBottom: 16 }}>
          <Mono color={T_NEW.ink56}
                style={{ display: "block", marginBottom: 8 }}>
            {t("customer.detail.history")}
          </Mono>
          {loading && (
            <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta,
                        padding: 8, margin: 0 }}>
              {t("customer.detail.history.loading")}
            </p>
          )}
          {!loading && orders !== null && orders.length === 0 && (
            <p style={{ color: T_NEW.ink38, fontSize: T_NEW.meta,
                        fontStyle: "italic", margin: 0 }}>
              {t("customer.detail.history.empty")}
            </p>
          )}
          {!loading && orders !== null && orders.length > 0 && (
            <div style={{ display: "grid", gap: 6 }}>
              {orders.map((o) => (
                <CustomerDetailOrderRow
                  key={o.id}
                  order={o}
                  onReorder={startReorder}
                  reordering={reorderingId === o.id}
                />
              ))}
            </div>
          )}
        </div>
        )}

        {/* Footer — actions */}
        <div style={{ display: "flex", gap: 8,
                      justifyContent: "space-between",
                      paddingTop: 8,
                      borderTop: `1px solid ${T_NEW.ink12}` }}>
          <button onClick={onDelete}
                  style={{ background: "transparent", color: "#a33",
                           border: `1px solid #a33${"33"}`,
                           padding: "8px 14px", borderRadius: T_NEW.r1,
                           cursor: "pointer", fontSize: T_NEW.meta,
                           fontWeight: 600 }}>
            {t("customer.detail.delete")}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" onClick={onClose}>{t("customer.detail.close")}</Btn>
            <Btn variant="accent" onClick={onEdit}>{t("customer.detail.edit")}</Btn>
          </div>
        </div>
      </div>

      {/* Reorder OrderModal — opens on top of the detail modal with
          the customer pre-pinned and the prior variant/qty pre-filled.
          previousUnitPriceCents drives the strike-through "было/сейчас"
          comparison so the reseller sees price drift before submitting. */}
      {reorderPost && reorderOrder && (
        <OrderModal
          post={reorderPost}
          onClose={closeReorder}
          onDone={onReorderDone}
          pinnedResellerCustomerId={customer.id}
          defaultVariant={reorderOrder.variant || ""}
          defaultQty={reorderOrder.qty || 1}
          previousUnitPriceCents={reorderOrder.unitPriceUsdCents || null}
        />
      )}
    </div>
  );
}

// Customer-bot invite row — shown inside the contact block of
// CustomerDetailModal. Two states:
//   • not linked → "Пригласить в бот" button. Click fetches a fresh
//     signed deeplink and copies it to the clipboard (and offers a
//     direct open).
//   • linked → small mono pill "Подключён к боту · с DD.MM.YYYY".
// Bot-link status loads on mount; the invite URL is requested lazily
// on first click so we don't burn HMAC cycles on rows the user
// never invites.
function CustomerBotInviteRow({ customerId, toast, t }) {
  const [status, setStatus] = useState(null);   // null=loading, false=not linked, obj=linked
  const [issuingInvite, setIssuingInvite] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.getCustomerBotLink(customerId);
        if (!cancelled) setStatus(r?.linked ? r : false);
      } catch {
        if (!cancelled) setStatus(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const issueInvite = async () => {
    setIssuingInvite(true);
    try {
      const r = await api.getCustomerInviteLink(customerId);
      if (!r?.url) {
        toast?.error?.(t("customer.detail.bot.notConfigured"));
        return;
      }
      try {
        await navigator.clipboard?.writeText(r.url);
        toast?.success?.(t("customer.detail.bot.linkCopied"));
      } catch {
        // Clipboard unavailable — just open the URL so the reseller
        // can grab it from the address bar.
      }
      window.open(r.url, "_blank");
    } catch (e) {
      toast?.error?.(e.message || t("customer.detail.bot.linkError"));
    } finally {
      setIssuingInvite(false);
    }
  };

  if (status === null) return null;        // brief flicker while loading
  if (status && status.startedAt) {
    const dt = new Date(status.startedAt * 1000);
    return (
      <div style={{
        marginTop: 8, padding: "6px 10px",
        background: T_NEW.bottleSoft,
        border: `1px solid ${T_NEW.bottle}33`,
        borderRadius: T_NEW.r1,
        display: "inline-flex", alignItems: "center", gap: 6,
      }}>
        <span style={{ width: 6, height: 6, borderRadius: 999,
                       background: T_NEW.bottle, display: "inline-block" }} />
        <Mono color={T_NEW.bottle} size={T_NEW.micro}
              style={{ fontWeight: 700 }}>
          {t("customer.detail.bot.connected", { date: dt.toLocaleDateString("ru-RU") })}
        </Mono>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={issueInvite}
        disabled={issuingInvite}
        style={{
          background: "transparent", color: T_NEW.persimmon,
          border: `1px dashed ${T_NEW.persimmon}66`,
          padding: "6px 10px", borderRadius: T_NEW.r1,
          fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
          letterSpacing: T_NEW.monoTrack, textTransform: "uppercase",
          fontWeight: 700, cursor: issuingInvite ? "wait" : "pointer",
          opacity: issuingInvite ? 0.7 : 1,
        }}>
        {issuingInvite ? t("customer.detail.bot.loading") : t("customer.detail.bot.invite")}
      </button>
      {/* Telegram /start payloads carry a 7-day signed expiry. After
          that the signature still verifies but the time check fails
          and the customer sees "Ссылка устарела." Surface this so
          resellers know they may need to re-issue. */}
      <Mono color={T_NEW.ink38} size={T_NEW.micro}
            style={{ display: "block", marginTop: 4 }}>
        {t("customer.detail.bot.inviteHint")}
      </Mono>
    </div>
  );
}

// One reminder card — date, optional note, and inline complete/delete
// actions. Overdue is signalled by a persimmon left border so the
// detail modal screams the pending action.
function ReminderRow({ r, onDone, onDelete, t }) {
  const dt = r.remindAt ? new Date(r.remindAt * 1000) : null;
  const overdue = dt && dt.getTime() < Date.now();
  return (
    <div style={{
      padding: "10px 12px",
      background: T_NEW.paperSoft,
      border: `1px solid ${T_NEW.ink12}`,
      borderLeft: `3px solid ${overdue ? T_NEW.persimmon : T_NEW.bottle}`,
      borderRadius: T_NEW.r1,
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                      fontWeight: 600 }}>
          {overdue ? t("customer.detail.reminder.overdue") : ""}
          {dt ? dt.toLocaleDateString("ru-RU", {
                  day: "2-digit", month: "long",
                }) : ""}
        </div>
        {r.note && (
          <Mono color={T_NEW.ink56} size={T_NEW.micro}
                style={{ display: "block", marginTop: 2 }}>
            {r.note}
          </Mono>
        )}
      </div>
      <button onClick={onDone}
              style={{
                background: T_NEW.bottle, color: "#fff",
                border: "none", padding: "6px 10px",
                borderRadius: T_NEW.r1,
                fontSize: T_NEW.micro, fontWeight: 600,
                fontFamily: T_NEW.fontSans, cursor: "pointer",
              }}>{t("customer.detail.reminder.done")}</button>
      <button onClick={onDelete} aria-label={t("customer.detail.reminder.delete")}
              style={{
                background: "transparent", color: T_NEW.ink56,
                border: `1px solid ${T_NEW.ink12}`,
                padding: "6px 10px",
                borderRadius: T_NEW.r1,
                fontSize: T_NEW.micro,
                fontFamily: T_NEW.fontSans, cursor: "pointer",
              }}>×</button>
    </div>
  );
}

function DetailStat({ label, value }) {
  return (
    <div style={{
      background: T_NEW.paperSoft,
      border: `1px solid ${T_NEW.ink12}`,
      borderRadius: T_NEW.r1,
      padding: "10px 12px",
      textAlign: "center",
    }}>
      <div style={{
        fontFamily: T_NEW.fontSerif,
        fontSize: T_NEW.displayMd,
        fontWeight: 500,
        color: T_NEW.ink,
        lineHeight: 1.1,
      }}>
        {value}
      </div>
      <Mono color={T_NEW.ink56} size={T_NEW.micro}
            style={{ display: "block", marginTop: 4 }}>
        {label}
      </Mono>
    </div>
  );
}

// CustomerAddressesPanel — per-customer book (migration 0054).
//
// Re-uses AddressRow + AddressForm from the AddressBookTab above so
// the visual is identical to the reseller's own address-book screen.
// The only difference is the badge key: customer addresses use
// is_primary, reseller's own use is_default.
function CustomerAddressesPanel({ customerId, toast }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankAddressForm(t));
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setRows(await api.listCustomerAddresses(customerId)); }
    catch (e) { toast.error(e.message); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [customerId]);

  const startNew = () => {
    setForm(blankAddressForm(t));
    setEditingId("new");
  };
  const startEdit = (row) => {
    setForm({
      label:        row.label || "",
      recipientName:row.recipientName || "",
      phone:        row.phone || "",
      address:      row.address || "",
      country:      row.country || "RU",
      // We map UI form.isDefault to API isPrimary for this panel,
      // so the AddressForm component stays generic.
      isDefault:    !!row.isPrimary,
    });
    setEditingId(row.id);
  };

  const save = async () => {
    if (!form.recipientName.trim() || !form.address.trim()) {
      toast.error(t("order.recipient.required"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        recipientName: form.recipientName.trim(),
        phone: form.phone.trim() || null,
        address: form.address.trim(),
        country: form.country.trim() || "RU",
        isPrimary: !!form.isDefault,
      };
      if (editingId === "new") {
        await api.createCustomerAddress(customerId, payload);
      } else {
        await api.updateCustomerAddress(customerId, editingId, payload);
      }
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("customer.addresses.deleteConfirm"))) return;
    try {
      await api.deleteCustomerAddress(customerId, id);
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const setPrimary = async (id) => {
    try {
      await api.updateCustomerAddress(customerId, id, { isPrimary: true });
      await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div>
      {rows == null ? (
        <div style={{ padding: 20, textAlign: "center", color: T_NEW.ink56 }}>…</div>
      ) : rows.length === 0 && editingId !== "new" ? (
        <div style={{ padding: 24, background: T_NEW.paperSoft,
                      border: `1px dashed ${T_NEW.ink12}`,
                      borderRadius: T_NEW.r2,
                      fontSize: 13, color: T_NEW.ink56,
                      fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                      textAlign: "center", marginBottom: 14 }}>
          {t("customer.addresses.empty")}
        </div>
      ) : (
        rows.map((a) => editingId === a.id ? (
          <AddressForm key={a.id} form={form} setForm={setForm}
                       saving={saving}
                       primaryLabel={t("customer.addresses.setPrimary")}
                       onSave={save}
                       onCancel={() => setEditingId(null)} />
        ) : (
          <AddressRow key={a.id} row={a} badgeKey="isPrimary"
                      badgeLabel={t("customer.addresses.primary")}
                      onEdit={() => startEdit(a)}
                      onDelete={() => remove(a.id)}
                      onMakeDefault={a.isPrimary ? null : () => setPrimary(a.id)}
                      setDefaultLabel={t("customer.addresses.setPrimary")} />
        ))
      )}

      {editingId === "new" && (
        <AddressForm form={form} setForm={setForm}
                     saving={saving}
                     primaryLabel={t("customer.addresses.setPrimary")}
                     onSave={save}
                     onCancel={() => setEditingId(null)} />
      )}

      {editingId == null && (
        <Btn variant="ghost" onClick={startNew}
             style={{ marginTop: 10 }}>
          + {t("customer.addresses.add")}
        </Btn>
      )}
    </div>
  );
}

// One order row inside the detail modal's timeline. Richer than the
// summary line we used in the old inline expand — adds placed-at
// date + a status badge with semantic colour (persimmon for active,
// green for delivered, ink56 for cancelled/unavailable).
function CustomerDetailOrderRow({ order, onReorder, reordering }) {
  const placed = order.placedAt ? new Date(order.placedAt * 1000) : null;
  const statusColor =
    order.status === "delivered"   ? "#3a7d3a"
    : order.status === "cancelled"   ? T_NEW.ink56
    : order.status === "unavailable" ? T_NEW.ink56
    :                                   T_NEW.persimmon;
  // Hide reorder for cancelled/unavailable rows — there's nothing
  // useful to re-place. Delivered + active orders both qualify.
  const canReorder = onReorder &&
    order.status !== "cancelled" &&
    order.status !== "unavailable";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 10px",
      background: T_NEW.paperSoft,
      border: `1px solid ${T_NEW.ink12}`,
      borderRadius: T_NEW.r1,
    }}>
      {order.post?.imageUrl ? (
        <img src={order.post.imageUrl} alt=""
             style={{ width: 44, height: 44, objectFit: "cover",
                      borderRadius: T_NEW.r1,
                      background: T_NEW.paperRaised,
                      flexShrink: 0 }} />
      ) : (
        <div style={{ width: 44, height: 44, flexShrink: 0,
                      background: T_NEW.paperRaised,
                      borderRadius: T_NEW.r1 }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                      whiteSpace: "nowrap", overflow: "hidden",
                      textOverflow: "ellipsis" }}>
          {order.post?.title || "(без названия)"}
        </div>
        <Mono color={T_NEW.ink56} size={T_NEW.micro}>
          {order.post?.channelSlug || "—"}
          {placed && ` · ${placed.toLocaleDateString("ru-RU")}`}
        </Mono>
      </div>
      <div style={{ flexShrink: 0, textAlign: "right" }}>
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                      fontWeight: 600 }}>
          ×{order.qty} · ${(order.totalUsdCents / 100).toFixed(2)}
        </div>
        <Mono color={statusColor} size={T_NEW.micro}
              style={{ display: "block", marginTop: 2, fontWeight: 700 }}>
          {order.status}
        </Mono>
      </div>
      {canReorder && (
        <button
          type="button"
          onClick={() => onReorder(order)}
          disabled={reordering}
          style={{
            flexShrink: 0,
            background: "transparent",
            color: T_NEW.persimmon,
            border: `1px solid ${T_NEW.ink12}`,
            padding: "6px 10px",
            borderRadius: T_NEW.r1,
            fontSize: T_NEW.micro,
            fontFamily: T_NEW.fontMono,
            letterSpacing: T_NEW.monoTrack,
            textTransform: "uppercase",
            fontWeight: 600,
            cursor: reordering ? "wait" : "pointer",
            opacity: reordering ? 0.6 : 1,
          }}
          title="Заказать тот же товар"
        >
          {reordering ? "…" : "Снова"}
        </button>
      )}
    </div>
  );
}

function CustomerFormModal({ customer, onClose, onSaved, toast }) {
  const isEdit = !!customer;
  const [name, setName] = useState(customer?.name || "");
  const [tg, setTg] = useState(customer?.telegramHandle || "");
  const [phone, setPhone] = useState(customer?.phone || "");
  const [category, setCategory] = useState(customer?.category || "");
  const [notes, setNotes] = useState(customer?.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Имя не может быть пустым"); return; }
    setSaving(true);
    try {
      const body = {
        name: trimmed,
        telegramHandle: tg.trim() || null,
        phone: phone.trim() || null,
        category: category.trim() || null,
        notes: notes.trim(),
      };
      if (isEdit) {
        await api.updateMyCustomer(customer.id, body);
      } else {
        await api.createMyCustomer(body);
      }
      await onSaved();
      toast.success(isEdit ? "Изменения сохранены" : "Клиент добавлен");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const { ref: formSheetRef, dy: formDy, dismissing: formClosing } =
    useSwipeDownDismiss(onClose);
  const formDragging = formDy > 0;
  const formScrim = formDragging
    ? Math.max(0.05, 0.5 - formDy / 900) : 0.5;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="cg-modal-bg"
      style={{
        position: "fixed", inset: 0,
        background: `rgba(20, 17, 13, ${formScrim})`,
        transition: formDragging ? "none" : "background-color 180ms",
        pointerEvents: formClosing ? "none" : "auto",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}>
      <div ref={formSheetRef} className="cg-modal-body"
           style={{
        background: T_NEW.paperRaised,
        borderRadius: T_NEW.r3,
        padding: 20,
        maxWidth: 440, width: "100%",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        transform: `translateY(${formDy}px)`,
        transition: formDragging ? "none"
                                  : "transform 220ms cubic-bezier(0.2,0,0.0,1.0)",
      }}>
        <div className="cg-modal-grab" aria-hidden />
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 14 }}>
          <Serif size={T_NEW.displaySm}>
            {isEdit ? "Изменить клиента" : "Новый клиент"}
          </Serif>
          <button onClick={onClose} aria-label="Close"
                  style={{ background: "none", border: "none", cursor: "pointer",
                           color: T_NEW.ink56, fontSize: 22, lineHeight: 1, padding: 4 }}>
            ×
          </button>
        </div>

        <FieldLabel>Имя</FieldLabel>
        <FieldInput value={name} onChange={setName}
                    placeholder="Иван Петров" autoFocus />

        <FieldLabel>Telegram</FieldLabel>
        <FieldInput value={tg} onChange={setTg}
                    placeholder="@username" />

        <FieldLabel>Телефон</FieldLabel>
        <FieldInput value={phone} onChange={setPhone}
                    placeholder="+7..." />

        <FieldLabel>Категория</FieldLabel>
        <FieldInput value={category} onChange={setCategory}
                    placeholder="VIP / постоянный / новый" />

        <FieldLabel>Заметки</FieldLabel>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Например: предпочитает крупный размер, всегда оплачивает картой"
          style={{
            width: "100%",
            padding: "8px 12px",
            border: `1px solid ${T_NEW.ink12}`,
            borderRadius: T_NEW.r1,
            fontFamily: T_NEW.fontSans,
            fontSize: T_NEW.body,
            background: T_NEW.paperSoft,
            color: T_NEW.ink,
            resize: "vertical",
            marginBottom: 14,
          }}
        />

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Отмена</Btn>
          <Btn variant="accent" onClick={save} disabled={saving}>
            {saving ? "..." : isEdit ? "Сохранить" : "Добавить"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <Mono color={T_NEW.ink56} style={{ display: "block",
                                       marginBottom: 4 }}>
      {children}
    </Mono>
  );
}

function FieldInput({ value, onChange, placeholder, autoFocus }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={!!autoFocus}
      style={{
        width: "100%",
        padding: "8px 12px",
        border: `1px solid ${T_NEW.ink12}`,
        borderRadius: T_NEW.r1,
        fontFamily: T_NEW.fontSans,
        fontSize: T_NEW.body,
        background: T_NEW.paperSoft,
        color: T_NEW.ink,
        marginBottom: 12,
      }}
    />
  );
}


// ─── PremiumTab — three-tier subscription (migration 0039) ───────────
// Free / Starter ($59/mo) / Pro ($99/mo) shown as three side-by-side
// cards. The user's current tier is highlighted; Subscribe buttons on
// other tiers open the order form (monthly/annual + notes textarea).
// Submission creates an in-app subscription_order — admin reviews and
// approves out-of-band payment from the admin panel. No more
// "DM @Buyergrambot" — keeps the flow in BuyerGram and gives admin a
// real queue with audit trail.
//
// Pro-only features (cashback, order management) are still in build;
// the tier is sold and tracked now so we can ship those features
// incrementally without renaming the tier later.
const TIERS = [
  {
    id: "free",
    label: "Free",
    monthly: 0,
    annual: 0,
    bullets: [
      "Карточки с водяным знаком BuyerGram",
      "Карточки с печатью BuyerGram",
      "Без прямой ссылки на источник",
    ],
  },
  {
    id: "starter",
    label: "Starter",
    monthly: 59,
    annual: 590, // 12 months for the price of 10 — same shape as `priceFor` server-side.
    bullets: [
      "Карточки без водяного знака",
      "Карточки без печати BuyerGram",
      "Прямая ссылка на источник",
    ],
  },
  {
    id: "pro",
    label: "Pro",
    monthly: 99,
    annual: 990,
    bullets: [
      "Всё из Starter",
      "BuyerGram кэшбек",
      "Система управления заказами",
    ],
  },
  // Pulse — analytics add-on. Independent from premium_tier so it
  // shows up as a 4th card on the Premium tab; subscribing here
  // bumps users.pulse_until instead of premium_until.
  {
    id: "pulse",
    label: "Pulse",
    monthly: 29,
    annual: 290,
    bullets: [
      "Что сейчас в тренде на платформе",
      "Самые горячие бренды и скидки",
      "Что вы упускаете в своих каналах",
      "Что ищут, но не находят",
    ],
    isAddOn: true,  // visual hint — Pulse is independent from tier
  },
];

function PremiumTab({ user }) {
  const currentTier = user.premiumTier || "free"; // 'free' | 'starter' | 'pro'
  const validUntilText = user.premiumUntil
    ? new Date(user.premiumUntil * 1000).toLocaleDateString("ru-RU", {
        day: "2-digit", month: "2-digit", year: "numeric",
      })
    : null;

  // The user clicked a Subscribe button on one of the upgrade tiers.
  // null = no form open; otherwise the tier id we're collecting an
  // order for. The form lets them pick monthly/annual and add notes.
  const [orderTier, setOrderTier] = useState(null);

  return (
    <div style={{ padding: "8px 0" }}>
      {/* If the user already has an active paid tier, show a banner
          at the top so they're not confused about which one. The
          tier-grid below still renders so they can compare or
          upgrade/downgrade. */}
      {currentTier !== "free" && (
        <div style={{
          background: T_NEW.persimmonSoft,
          borderRadius: T_NEW.r2,
          padding: "12px 16px",
          marginBottom: 16,
          border: `1px solid ${T_NEW.persimmon}`,
        }}>
          <Mono color={T_NEW.persimmon}
                style={{ display: "block", marginBottom: 4, fontWeight: 700 }}>
            ВАШ ТАРИФ — {currentTier.toUpperCase()} ✓
          </Mono>
          {validUntilText && (
            <p style={{ color: T_NEW.ink, fontSize: T_NEW.meta, margin: 0 }}>
              Активен до <strong>{validUntilText}</strong>
            </p>
          )}
        </div>
      )}

      {/* Tier grid — Free / Starter / Pro. Pulse is rendered
          separately below as an add-on banner since it's
          independent from the tier ladder (migration 0043). */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 10,
        marginBottom: 16,
      }}>
        {TIERS.filter((t) => !t.isAddOn).map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            isCurrent={tier.id === currentTier}
            onSubscribe={() => setOrderTier(tier.id)}
          />
        ))}
      </div>

      {/* Pulse add-on — full-width banner. Independent from premium
          tier so it has its own active-state indicator (user.isPulseActive)
          and its own subscribe path. */}
      {TIERS.filter((t) => t.isAddOn).map((tier) => (
        <PulseAddOnBanner
          key={tier.id}
          tier={tier}
          isActive={!!user.isPulseActive}
          validUntilText={user.pulseUntil
            ? new Date(user.pulseUntil * 1000).toLocaleDateString("ru-RU", {
                day: "2-digit", month: "2-digit", year: "numeric",
              })
            : null}
          onSubscribe={() => setOrderTier(tier.id)}
        />
      ))}

      <SubscriptionOrders />

      {orderTier && (
        <SubscriptionOrderModal
          tierId={orderTier}
          onClose={() => setOrderTier(null)}
        />
      )}
    </div>
  );
}

// Full-width banner for the Pulse add-on (migration 0043). Visually
// separated from the tier grid since Pulse is bought independently
// from premium_tier. Active state shows 'valid until DD.MM.YYYY';
// inactive shows the price + 'Подключить' button.
function PulseAddOnBanner({ tier, isActive, validUntilText, onSubscribe }) {
  return (
    <div style={{
      background: isActive ? T_NEW.persimmonSoft : T_NEW.paperSoft,
      border: `1px solid ${isActive ? T_NEW.persimmon : T_NEW.ink12}`,
      borderRadius: T_NEW.r2,
      padding: "14px 16px",
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      gap: 14,
      flexWrap: "wrap",
    }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <Mono color={isActive ? T_NEW.persimmon : T_NEW.ink56}
              size={T_NEW.micro}
              style={{ display: "block", fontWeight: 700, marginBottom: 4 }}>
          ДОБАВКА · {tier.label.toUpperCase()}{isActive ? " ✓" : ""}
        </Mono>
        <div style={{ fontSize: T_NEW.body, color: T_NEW.ink, marginBottom: 6 }}>
          {tier.bullets.join(" · ")}
        </div>
        {isActive && validUntilText && (
          <Mono color={T_NEW.ink56} size={T_NEW.micro}>
            Активен до {validUntilText}
          </Mono>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{
          fontFamily: T_NEW.fontSerif,
          fontSize: T_NEW.displaySm,
          fontWeight: 500,
          color: T_NEW.ink,
          lineHeight: 1.1,
        }}>
          ${tier.monthly}
        </div>
        <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginBottom: 6 }}>
          /мес или ${tier.annual}/год
        </div>
        {!isActive && (
          <Btn variant="accent" onClick={onSubscribe}>Подключить</Btn>
        )}
      </div>
    </div>
  );
}

function TierCard({ tier, isCurrent, onSubscribe }) {
  const highlight = tier.id === "starter"; // sweet-spot tier — most users go here
  const borderColor = isCurrent ? T_NEW.persimmon
                    : highlight ? T_NEW.persimmon
                    : T_NEW.ink12;
  const bg = isCurrent ? T_NEW.persimmonSoft : T_NEW.paperSoft;

  return (
    <div style={{
      background: bg,
      border: `1px solid ${borderColor}`,
      borderRadius: T_NEW.r2,
      padding: "14px 12px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      minHeight: 280,
    }}>
      <Mono color={isCurrent ? T_NEW.persimmon : T_NEW.ink56}
            size={T_NEW.micro}
            style={{ fontWeight: 700, textAlign: "center" }}>
        {tier.label.toUpperCase()}{isCurrent ? " ✓" : ""}
      </Mono>
      <div style={{ textAlign: "center" }}>
        {tier.monthly > 0 ? (
          <>
            <div style={{
              fontFamily: T_NEW.fontSerif,
              fontSize: T_NEW.displaySm,
              fontWeight: 500,
              color: T_NEW.ink,
              lineHeight: 1.1,
            }}>
              ${tier.monthly}
            </div>
            <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56 }}>
              /месяц или ${tier.annual}/год
            </div>
          </>
        ) : (
          <div style={{
            fontFamily: T_NEW.fontSerif,
            fontSize: T_NEW.displaySm,
            fontWeight: 500,
            color: T_NEW.ink56,
            lineHeight: 1.1,
          }}>
            $0
          </div>
        )}
      </div>
      <ul style={{
        margin: 0,
        paddingLeft: 16,
        fontSize: T_NEW.micro,
        color: T_NEW.ink,
        lineHeight: 1.6,
        flex: 1,
      }}>
        {tier.bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
      {!isCurrent && tier.id !== "free" && (
        <Btn variant="accent" onClick={onSubscribe}>Оформить</Btn>
      )}
      {isCurrent && (
        <div style={{
          textAlign: "center",
          fontSize: T_NEW.micro,
          color: T_NEW.persimmon,
          fontWeight: 700,
        }}>
          АКТИВЕН
        </div>
      )}
    </div>
  );
}

// ─── SubscriptionOrders ──────────────────────────────────────────────
// Renders the customer's own order history below the tier grid so they
// can see what they've requested and the status (pending / approved /
// rejected / cancelled). Empty list collapses to nothing — no need
// for an empty state.
function SubscriptionOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const reload = async () => {
    setLoading(true);
    try {
      const list = await api.listMySubscriptionOrders();
      setOrders(list || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const cancel = async (orderId) => {
    if (!await toast.confirm("Отменить заявку?")) return;
    try {
      await api.cancelMySubscriptionOrder(orderId);
      await reload();
      toast.success("Заявка отменена");
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading || orders.length === 0) return null;

  const statusLabel = {
    pending:   { text: "На рассмотрении", color: T_NEW.persimmon },
    approved:  { text: "Одобрено",        color: "#3a7d3a" },
    rejected:  { text: "Отклонено",       color: "#a33" },
    cancelled: { text: "Отменено",        color: T_NEW.ink56 },
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
        Мои заявки
      </Mono>
      <div style={{ display: "grid", gap: 6 }}>
        {orders.map((o) => {
          const s = statusLabel[o.status] || { text: o.status, color: T_NEW.ink56 };
          return (
            <div key={o.id} style={{
              padding: "8px 12px",
              background: T_NEW.paperSoft,
              border: `1px solid ${T_NEW.ink12}`,
              borderRadius: T_NEW.r1,
              fontSize: T_NEW.meta,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{o.tier.toUpperCase()}</strong>
                {" · "}
                {o.billingPeriod === "annual" ? "год" : "месяц"}
                {" · "}
                <span style={{ color: T_NEW.ink56 }}>
                  ${(o.amountCents / 100).toFixed(0)}
                </span>
              </div>
              <span style={{
                color: s.color,
                fontSize: T_NEW.micro,
                fontWeight: 700,
                fontFamily: T_NEW.fontMono,
                textTransform: "uppercase",
              }}>
                {s.text}
              </span>
              {o.status === "pending" && (
                <button onClick={() => cancel(o.id)}
                        style={{ background: "none", border: "none", cursor: "pointer",
                                 color: T_NEW.ink56, fontSize: T_NEW.micro }}>
                  ✕
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── SubscriptionOrderModal ──────────────────────────────────────────
// Inline order form. Lives inside SettingsModal (already a modal), so
// rendered as an absolutely-positioned card over the tier grid rather
// than a stacked Modal — keeps the visual focus tight.
function SubscriptionOrderModal({ tierId, onClose }) {
  const toast = useToast();
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const tier = TIERS.find((t) => t.id === tierId);
  if (!tier) return null;
  const amount = billingPeriod === "annual" ? tier.annual : tier.monthly;

  const submit = async () => {
    setSaving(true);
    try {
      await api.createSubscriptionOrder({
        tier: tierId,
        billingPeriod,
        notes: notes.trim() || undefined,
      });
      toast.success("Заявка создана. Админ свяжется с вами.");
      onClose();
      // Trigger a reload of the orders list. The parent component
      // doesn't expose a refetch callback, so we just suggest the
      // user reload — simpler than threading a refresh prop through.
      window.location.reload();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const { ref: subSheetRef, dy: subDy, dismissing: subClosing } =
    useSwipeDownDismiss(onClose);
  const subDragging = subDy > 0;
  const subScrim = subDragging
    ? Math.max(0.05, 0.5 - subDy / 900) : 0.5;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="cg-modal-bg"
      style={{
        position: "fixed", inset: 0,
        background: `rgba(20, 17, 13, ${subScrim})`,
        transition: subDragging ? "none" : "background-color 180ms",
        pointerEvents: subClosing ? "none" : "auto",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 100, padding: 20,
      }}>
      <div ref={subSheetRef} className="cg-modal-body"
           style={{
        background: T_NEW.paperRaised,
        borderRadius: T_NEW.r3,
        padding: 20,
        maxWidth: 440, width: "100%",
        maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        transform: `translateY(${subDy}px)`,
        transition: subDragging ? "none"
                                 : "transform 220ms cubic-bezier(0.2,0,0.0,1.0)",
      }}>
        <div className="cg-modal-grab" aria-hidden />
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 12 }}>
          <Serif size={T_NEW.displaySm}>Оформить {tier.label}</Serif>
          <button onClick={onClose} aria-label="Close"
                  style={{ background: "none", border: "none", cursor: "pointer",
                           color: T_NEW.ink56, fontSize: 22, lineHeight: 1, padding: 4 }}>
            ×
          </button>
        </div>

        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
          Период
        </Mono>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                      gap: 8, marginBottom: 16 }}>
          <button onClick={() => setBillingPeriod("monthly")}
                  style={{
                    padding: "10px 12px",
                    background: billingPeriod === "monthly" ? T_NEW.persimmonSoft : T_NEW.paperSoft,
                    border: `1px solid ${billingPeriod === "monthly" ? T_NEW.persimmon : T_NEW.ink12}`,
                    borderRadius: T_NEW.r1,
                    cursor: "pointer",
                    fontFamily: T_NEW.fontSans,
                  }}>
            <div style={{ fontWeight: 700 }}>Месяц</div>
            <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56 }}>${tier.monthly}/мес</div>
          </button>
          <button onClick={() => setBillingPeriod("annual")}
                  style={{
                    padding: "10px 12px",
                    background: billingPeriod === "annual" ? T_NEW.persimmonSoft : T_NEW.paperSoft,
                    border: `1px solid ${billingPeriod === "annual" ? T_NEW.persimmon : T_NEW.ink12}`,
                    borderRadius: T_NEW.r1,
                    cursor: "pointer",
                    fontFamily: T_NEW.fontSans,
                  }}>
            <div style={{ fontWeight: 700 }}>Год</div>
            <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56 }}>
              ${tier.annual} · −17%
            </div>
          </button>
        </div>

        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          Комментарий (необязательно)
        </Mono>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Например: оплачу криптой, дайте реквизиты"
          style={{
            width: "100%",
            padding: "8px 12px",
            border: `1px solid ${T_NEW.ink12}`,
            borderRadius: T_NEW.r1,
            fontFamily: T_NEW.fontSans,
            fontSize: T_NEW.body,
            background: T_NEW.paperSoft,
            color: T_NEW.ink,
            resize: "vertical",
            marginBottom: 14,
          }}
        />

        <div style={{
          background: T_NEW.paperSoft,
          padding: "10px 12px",
          borderRadius: T_NEW.r1,
          fontSize: T_NEW.meta,
          marginBottom: 14,
          color: T_NEW.ink56,
          lineHeight: 1.5,
        }}>
          После отправки заявки админ свяжется с вами для оплаты.
          Когда оплата подтвердится, тариф активируется автоматически.
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Mono color={T_NEW.ink} style={{ fontSize: T_NEW.body, fontWeight: 700 }}>
            ИТОГО: ${amount}
          </Mono>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" onClick={onClose} disabled={saving}>Отмена</Btn>
            <Btn variant="accent" onClick={submit} disabled={saving}>
              {saving ? "..." : "Отправить заявку"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── ForwardStatsTab — reseller analytics ────────────────────────────
// Hits GET /api/forwards/stats once on mount, renders three blocks:
//   1. Totals row — week / month / all-time successful deliveries.
//   2. Top forwarded cards — thumbnail + brand/title + count, click
//      jumps to the NR source URL when available.
//   3. Top channels by activity — friendly label + count.
// Empty state ("you haven't forwarded anything yet") guides new
// resellers to the channel feed. No charts in v1 — the dataset at
// Phase 0 scale isn't worth the chart-library footprint.
//
// Premium-gated 2026-06-05: free tier sees an upgrade pitch with a
// jump-to-Premium-tab CTA instead of the stats. Server endpoint
// /api/forwards/stats isn't role-gated yet (any customer can hit
// it), but the UI doesn't surface that path for free tier and the
// data is harmless if scraped (your own forwards only).
export function ForwardStatsTab({ toast, user, onUpgrade }) {
  const isPremium = !!user?.isPremium;

  // Hooks must be called unconditionally — keep them above any early
  // return so the upgrade-prompt branch doesn't trigger a hook order
  // change on a re-render after the user upgrades.
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Skip the API call entirely on free tier — saves a wasted
    // request and any future role-check on the endpoint.
    if (!isPremium) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const s = await api.getForwardStats();
        setStats(s);
      } catch (e) {
        toast?.error?.(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isPremium]);

  if (!isPremium) {
    return (
      <div style={{ padding: "20px 0" }}>
        <div style={{
          background: T_NEW.persimmonSoft,
          border: `1px solid ${T_NEW.persimmon}`,
          borderRadius: T_NEW.r2,
          padding: "20px 22px",
        }}>
          <Mono color={T_NEW.persimmon}
                style={{ display: "block", marginBottom: 8, fontWeight: 700 }}>
            ФУНКЦИЯ PREMIUM
          </Mono>
          <Serif size={T_NEW.displaySm}
                 style={{ marginBottom: 10, fontWeight: 500, lineHeight: 1.25 }}>
            Аналитика пересылки
          </Serif>
          <p style={{ color: T_NEW.ink, fontSize: T_NEW.meta, lineHeight: 1.55,
                      margin: "0 0 16px 0" }}>
            Сколько карточек вы переслали за неделю, месяц и всё время,
            какие товары и каналы работают лучше всего — доступно с тарифа
            Starter и выше.
          </p>
          <Btn variant="accent" onClick={onUpgrade}>
            Открыть Premium
          </Btn>
        </div>
      </div>
    );
  }

  if (loading) return <p style={{ color: T_NEW.ink56, padding: 16 }}>Загрузка…</p>;
  if (!stats) return <p style={{ color: T_NEW.ink56, padding: 16 }}>Не удалось загрузить статистику.</p>;

  const { totals, topPosts, topTargets } = stats;
  const totalAll = totals?.allTime ?? 0;

  if (totalAll === 0) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center" }}>
        <p style={{ fontSize: T_NEW.body, color: T_NEW.ink, marginBottom: 4 }}>
          Здесь будет ваша статистика пересылки.
        </p>
        <p style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, lineHeight: 1.5 }}>
          Откройте любой канал, выберите карточки и нажмите «Переслать».<br />
          Когда отправите первую — она появится здесь.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 0" }}>
      {/* ── Totals row ────────────────────────────────────────────── */}
      <section style={{ marginBottom: 24 }}>
        <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
          Карточки отправлены
        </Mono>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <StatCell value={totals.week}    label="За неделю" />
          <StatCell value={totals.month}   label="За месяц" />
          <StatCell value={totals.allTime} label="Всего" />
        </div>
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.micro, marginTop: 6, lineHeight: 1.5 }}>
          Считаем каждую успешную доставку «карточка → канал» отдельно.
          Одна карточка, отправленная в три канала, считается за три.
        </p>
      </section>

      {/* ── Top forwarded cards ──────────────────────────────────── */}
      {topPosts.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
            Самые отправляемые карточки
          </Mono>
          <div style={{
            display: "grid", gap: 6,
            background: T_NEW.paperSoft,
            padding: 8, borderRadius: T_NEW.r1,
          }}>
            {topPosts.map((p) => (
              <a
                key={p.postId}
                href={p.sourceUrl || "#"}
                target={p.sourceUrl ? "_blank" : undefined}
                rel="noopener noreferrer"
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "6px 8px", borderRadius: T_NEW.r1,
                  background: T_NEW.paperRaised,
                  color: T_NEW.ink, textDecoration: "none",
                  border: `1px solid ${T_NEW.ink12}`,
                  cursor: p.sourceUrl ? "pointer" : "default",
                }}>
                {p.imageUrl ? (
                  <img src={p.imageUrl} alt=""
                       style={{ width: 44, height: 44, objectFit: "cover",
                                borderRadius: T_NEW.r1, flexShrink: 0,
                                background: T_NEW.paperSoft }} />
                ) : (
                  <div style={{ width: 44, height: 44, background: T_NEW.paperSoft,
                                borderRadius: T_NEW.r1, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                  {p.brand && (
                    <Mono color={T_NEW.ink56} size={T_NEW.micro}
                          style={{ display: "block",
                                   whiteSpace: "nowrap",
                                   overflow: "hidden",
                                   textOverflow: "ellipsis" }}>
                      {p.brand}
                    </Mono>
                  )}
                  <div style={{ fontSize: T_NEW.meta,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis" }}>
                    {p.title}
                  </div>
                </div>
                <span style={{
                  fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta,
                  fontWeight: 700, color: T_NEW.persimmon,
                  flexShrink: 0,
                }}>
                  ×{p.count}
                </span>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* ── Top targets ──────────────────────────────────────────── */}
      {topTargets.length > 0 && (
        <section>
          <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 8 }}>
            Активные каналы
          </Mono>
          <div style={{
            display: "grid", gap: 6,
            background: T_NEW.paperSoft,
            padding: 8, borderRadius: T_NEW.r1,
          }}>
            {topTargets.map((t) => (
              <div key={t.targetId}
                   style={{
                     display: "flex", alignItems: "center", gap: 10,
                     padding: "8px 10px", borderRadius: T_NEW.r1,
                     background: T_NEW.paperRaised,
                     border: `1px solid ${T_NEW.ink12}`,
                   }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: T_NEW.body, color: T_NEW.ink,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis" }}>
                    {t.label}
                  </div>
                  <Mono color={T_NEW.ink56} size={T_NEW.micro}>
                    {t.kind === "telegram" ? "TELEGRAM"
                      : t.kind === "max"   ? "MAX"
                      : "—"}
                    {t.chatId && t.label !== t.chatId ? ` · ${t.chatId}` : ""}
                  </Mono>
                </div>
                <span style={{
                  fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta,
                  fontWeight: 700, color: T_NEW.persimmon,
                  flexShrink: 0,
                }}>
                  ×{t.count}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Pulse section ──────────────────────────────────────────
          Platform-wide analytics gated on users.pulse_until (independent
          add-on, migration 0043). Premium-tier users without Pulse see
          an upsell card; subscribers see the full widget surface. */}
      <PulseSection user={user} onSubscribe={onUpgrade} />
    </div>
  );
}

// ─── PulseSection — platform analytics for Pulse subscribers ────────
// Renders five widgets that surface what's happening across the
// whole BuyerGram platform: trending cards, hot brands, discount
// sweet spot, what-you-missing, demand gaps. Read-only; the data is
// driven by the Pulse-gated API endpoints in routes/pulse.ts.
function PulseSection({ user, onSubscribe }) {
  const hasPulse = !!user?.isPulseActive;
  const validUntilText = user?.pulseUntil
    ? new Date(user.pulseUntil * 1000).toLocaleDateString("ru-RU", {
        day: "2-digit", month: "2-digit", year: "numeric",
      })
    : null;

  if (!hasPulse) {
    return (
      <div style={{
        background: T_NEW.persimmonSoft,
        border: `1px solid ${T_NEW.persimmon}`,
        borderRadius: T_NEW.r2,
        padding: "20px 22px",
        marginTop: 16,
      }}>
        <Mono color={T_NEW.persimmon}
              style={{ display: "block", marginBottom: 8, fontWeight: 700 }}>
          PULSE — ДОПОЛНИТЕЛЬНО
        </Mono>
        <Serif size={T_NEW.displaySm}
               style={{ marginBottom: 8, fontWeight: 500, lineHeight: 1.25 }}>
          Аналитика всей платформы — $29/мес
        </Serif>
        <p style={{ color: T_NEW.ink, fontSize: T_NEW.meta, lineHeight: 1.55,
                    margin: "0 0 12px 0" }}>
          Тренды всей платформы — какие товары сейчас движутся, какие
          бренды и скидки работают лучше всего, какие карточки вы
          упускаете. Все цифры агрегированные и анонимные. Доступно как
          добавка к любому тарифу (включая Free).
        </p>
        <Btn variant="accent" onClick={onSubscribe}>
          Подключить Pulse
        </Btn>
      </div>
    );
  }

  return <PulseWidgets validUntilText={validUntilText} />;
}

// Loads + renders all five Pulse widgets. Each widget is its own
// fetch + render block so a slow query on one doesn't block the
// others. Errors render in-place (small ink56 text), not as full-tab
// blockers.
function PulseWidgets({ validUntilText }) {
  const [trending, setTrending] = useState(null);
  const [brands, setBrands] = useState(null);
  const [buckets, setBuckets] = useState(null);
  const [missing, setMissing] = useState(null);
  const [gaps, setGaps] = useState(null);

  useEffect(() => {
    api.getPulseTrending().then(setTrending).catch(() => setTrending([]));
    api.getPulseHotBrands().then(setBrands).catch(() => setBrands([]));
    api.getPulseDiscountBuckets().then(setBuckets).catch(() => setBuckets([]));
    api.getPulseMissing().then(setMissing).catch(() => setMissing([]));
    api.getPulseDemandGaps().then(setGaps).catch(() => setGaps([]));
  }, []);

  return (
    <>
      <div style={{
        background: T_NEW.persimmonSoft,
        border: `1px solid ${T_NEW.persimmon}`,
        borderRadius: T_NEW.r2,
        padding: "12px 16px",
        marginTop: 16,
        marginBottom: 16,
      }}>
        <Mono color={T_NEW.persimmon}
              style={{ display: "block", fontWeight: 700, marginBottom: 4 }}>
          PULSE ✓
        </Mono>
        {validUntilText && (
          <p style={{ margin: 0, fontSize: T_NEW.meta, color: T_NEW.ink }}>
            Активен до <strong>{validUntilText}</strong>
          </p>
        )}
      </div>

      {/* Widget 1 — Trending cards */}
      <PulseList
        title="Сейчас в тренде"
        subtitle="Топ карточек по пересылкам за 7 дней"
        rows={trending}
        renderRow={(r) => (
          <PulseCardRow
            key={r.postId}
            imageUrl={r.imageUrl}
            brand={r.brand}
            title={r.title}
            channelSlug={r.channelSlug}
            publicCode={r.publicCode}
            metric={`×${r.forwards7d}`}
            delta={r.deltaPct}
          />
        )}
      />

      {/* Widget 2 — Hot brands */}
      <PulseList
        title="Бренды месяца"
        subtitle="Доля пересылок по брендам за 30 дней"
        rows={brands}
        renderRow={(b) => (
          <PulseStatRow
            key={b.brand}
            label={b.brand}
            metric={`${b.sharePct}%`}
            secondary={`${b.forwards} пересылок · ${b.resellerCount} реселлеров`}
          />
        )}
      />

      {/* Widget 3 — Discount sweet spot */}
      <PulseList
        title="Что покупают по скидке"
        subtitle="Распределение пересылок по диапазону скидки"
        rows={buckets}
        renderRow={(b) => (
          <PulseStatRow
            key={b.bucket}
            label={b.bucket}
            metric={`${b.forwards}`}
            secondary={`${b.resellerCount} реселлеров`}
          />
        )}
      />

      {/* Widget 4 — What you're missing */}
      <PulseList
        title="Вы пропускаете"
        subtitle="Карточки, которые активно пересылают другие, а вы — нет"
        rows={missing}
        renderRow={(r) => (
          <PulseCardRow
            key={r.postId}
            imageUrl={r.imageUrl}
            brand={r.brand}
            title={r.title}
            channelSlug={r.channelSlug}
            publicCode={r.publicCode}
            metric={`×${r.othersForwards}`}
          />
        )}
      />

      {/* Widget 5 — Demand gaps from search queries */}
      <PulseList
        title="Что ищут, но не находят"
        subtitle="Запросы реселлеров с малым числом совпадений за 7 дней"
        rows={gaps}
        emptyText="Данных пока недостаточно — поиск только начал накапливать историю."
        renderRow={(g) => (
          <PulseStatRow
            key={g.query}
            label={`«${g.query}»`}
            metric={`${g.searchCount} запросов`}
            secondary={g.avgMatched != null
              ? `${g.resellerCount} реселлеров · в среднем ${g.avgMatched} совпадений`
              : `${g.resellerCount} реселлеров · совпадений почти нет`}
          />
        )}
      />
    </>
  );
}

// ─── Pulse render helpers ──────────────────────────────────────────
function PulseList({ title, subtitle, rows, renderRow, emptyText }) {
  return (
    <section style={{ marginBottom: 20 }}>
      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 4 }}>
        {title}
      </Mono>
      {subtitle && (
        <p style={{ margin: "0 0 8px 0", fontSize: T_NEW.micro, color: T_NEW.ink56 }}>
          {subtitle}
        </p>
      )}
      {rows === null && (
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, padding: 8 }}>
          Загрузка…
        </p>
      )}
      {rows !== null && rows.length === 0 && (
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.meta, padding: 8,
                    fontStyle: "italic" }}>
          {emptyText || "Пока нет данных по этой метрике."}
        </p>
      )}
      {rows !== null && rows.length > 0 && (
        <div style={{
          display: "grid", gap: 6,
          background: T_NEW.paperSoft,
          padding: 8, borderRadius: T_NEW.r1,
        }}>
          {rows.map(renderRow)}
        </div>
      )}
    </section>
  );
}

// Row with a thumbnail + brand + title + metric. Used for Trending,
// Missing-from-your-feed.
function PulseCardRow({ imageUrl, brand, title, channelSlug, publicCode, metric, delta }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "6px 8px", borderRadius: T_NEW.r1,
      background: T_NEW.paperRaised,
      border: `1px solid ${T_NEW.ink12}`,
    }}>
      {imageUrl ? (
        <img src={imageUrl} alt=""
             style={{ width: 44, height: 44, objectFit: "cover",
                      borderRadius: T_NEW.r1, flexShrink: 0,
                      background: T_NEW.paperSoft }} />
      ) : (
        <div style={{ width: 44, height: 44, background: T_NEW.paperSoft,
                      borderRadius: T_NEW.r1, flexShrink: 0 }} />
      )}
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
        {brand && (
          <Mono color={T_NEW.ink56} size={T_NEW.micro}
                style={{ display: "block",
                         whiteSpace: "nowrap",
                         overflow: "hidden",
                         textOverflow: "ellipsis" }}>
            {brand}{publicCode ? ` · ${publicCode}` : ""}
          </Mono>
        )}
        <div style={{ fontSize: T_NEW.meta,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis" }}>
          {title}
        </div>
        <Mono color={T_NEW.ink38} size={T_NEW.micro}>
          {channelSlug}
        </Mono>
      </div>
      <div style={{ flexShrink: 0, textAlign: "right" }}>
        <span style={{
          fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta,
          fontWeight: 700, color: T_NEW.persimmon,
        }}>
          {metric}
        </span>
        {delta != null && (
          <div style={{ fontSize: T_NEW.micro,
                        color: delta >= 0 ? "#3a7d3a" : T_NEW.ink56 }}>
            {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}%
          </div>
        )}
      </div>
    </div>
  );
}

// Row with just label + metric. Used for hot brands, discount buckets,
// demand gaps.
function PulseStatRow({ label, metric, secondary }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 10px", borderRadius: T_NEW.r1,
      background: T_NEW.paperRaised,
      border: `1px solid ${T_NEW.ink12}`,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.body, color: T_NEW.ink,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis" }}>
          {label}
        </div>
        {secondary && (
          <Mono color={T_NEW.ink56} size={T_NEW.micro}>
            {secondary}
          </Mono>
        )}
      </div>
      <span style={{
        fontFamily: T_NEW.fontMono, fontSize: T_NEW.meta,
        fontWeight: 700, color: T_NEW.persimmon,
        flexShrink: 0,
      }}>
        {metric}
      </span>
    </div>
  );
}

// Small numeric stat card used by the totals row above.
function StatCell({ value, label }) {
  return (
    <div style={{
      background: T_NEW.paperSoft,
      borderRadius: T_NEW.r1,
      padding: "12px 10px",
      textAlign: "center",
      border: `1px solid ${T_NEW.ink12}`,
    }}>
      <div style={{
        fontFamily: T_NEW.fontSerif,
        fontSize: T_NEW.displayMd,
        fontWeight: 500,
        color: T_NEW.ink,
        lineHeight: 1.1,
      }}>
        {(value ?? 0).toLocaleString("ru-RU")}
      </div>
      <Mono color={T_NEW.ink56} size={T_NEW.micro}
            style={{ display: "block", marginTop: 4 }}>
        {label}
      </Mono>
    </div>
  );
}


// ─── CaptionPreview — render a sample caption with current settings ──
// Mirrors the server-side renderCaption + buildCaptionVars logic in
// apps/api/src/routes/forwards.ts so the preview the reseller sees
// here matches what their audience will see in Telegram. Keep these
// two in sync — if a new {{variable}} ships server-side, add it here.
//
// We render against a fixed sample post (Nike Sportswear Tech Fleece —
// invented numbers) so the preview is stable across users and doesn't
// require an extra API call. The exact item doesn't matter; the goal
// is showing the *shape* of the caption.
function renderSample(template, vars) {
  return template.replace(/\{\{(\w+)\}\}/g, (m, name) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : m,
  );
}

const SAMPLE_POST = {
  brand: "Nike Sportswear",
  title: "Tech Fleece Crew Sweatshirt",
  description: "Мягкая флисовая толстовка с плотной посадкой. Идеальна для прохладной погоды и многослойных образов.",
  priceUsd: 89.95,
  msrpUsd: 130.00,
  publicCode: "BG7F3K9P",  // matches the 8-char Crockford alphabet
};

function CaptionPreview({ template, customText, fxRate }) {
  const rate = Number.isFinite(fxRate) && fxRate > 0 ? fxRate : 0;
  const priceRub = SAMPLE_POST.priceUsd * rate;
  // MSRP in rubles uses the SAME reseller FX rate as price_rub —
  // mirrors the server-side buildCaptionVars math so the preview here
  // matches what customers actually see. Mixing $ and ₽ in one block
  // creates a backwards discount illusion when the reseller's rate has
  // markup baked in.
  const msrpRub = SAMPLE_POST.msrpUsd * rate;
  const discountPct = SAMPLE_POST.msrpUsd > SAMPLE_POST.priceUsd
    ? Math.round((1 - SAMPLE_POST.priceUsd / SAMPLE_POST.msrpUsd) * 100)
    : 0;
  const vars = {
    brand: SAMPLE_POST.brand,
    title: SAMPLE_POST.title,
    description: SAMPLE_POST.description,
    price_usd: SAMPLE_POST.priceUsd.toFixed(2),
    price_rub: rate > 0 ? Math.round(priceRub).toLocaleString("ru-RU") : "—",
    msrp_usd: SAMPLE_POST.msrpUsd.toFixed(2),
    msrp_rub: rate > 0 ? Math.round(msrpRub).toLocaleString("ru-RU") : "—",
    discount_pct: String(discountPct),
    direct_link: "",
    public_code: SAMPLE_POST.publicCode,
  };
  let body = renderSample(template, vars).trim();
  const custom = (customText || "").trim();
  if (custom) body = `${body}\n\n${renderSample(custom, vars)}`.trim();
  if (!body) body = "(пусто — отметьте хотя бы один пункт или добавьте свой текст)";

  return (
    <div style={{ marginTop: 14 }}>
      <Mono color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
        Предпросмотр подписи
      </Mono>
      <pre style={{
        background: T_NEW.paperSoft,
        border: `1px solid ${T_NEW.ink12}`,
        borderRadius: T_NEW.r1,
        padding: "10px 12px",
        fontFamily: T_NEW.fontSans,
        fontSize: T_NEW.body,
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        margin: 0,
        color: T_NEW.ink,
        lineHeight: 1.5,
      }}>
        {body}
      </pre>
      <Mono color={T_NEW.ink38} size={T_NEW.micro} style={{ display: "block", marginTop: 6 }}>
        Пример: {SAMPLE_POST.brand} · ${SAMPLE_POST.priceUsd.toFixed(2)} · курс ₽
        {rate > 0 ? rate.toFixed(2) : "—"}
      </Mono>
    </div>
  );
}


// ─── Toggle — labeled checkbox row used by the Forward settings ──────
function Toggle({ label, checked, onChange }) {
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 8,
      cursor: "pointer", fontSize: T_NEW.body, color: T_NEW.ink,
      padding: "4px 0",
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        style={{ accentColor: T_NEW.persimmon, width: 16, height: 16 }}
      />
      <span>{label}</span>
    </label>
  );
}
